//----------------------
// <auto-generated>
//     Generated using the NSwag toolchain v14.6.3.0 (NJsonSchema v11.5.2.0 (Newtonsoft.Json v13.0.0.0)) (http://NSwag.org)
// </auto-generated>
//----------------------

/* eslint-disable */
// ReSharper disable InconsistentNaming

import { mergeMap as _observableMergeMap, catchError as _observableCatch } from 'rxjs/operators';
import { Observable, throwError as _observableThrow, of as _observableOf } from 'rxjs';
import { Injectable, Inject, Optional, InjectionToken, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { ASSESSMENT_API_BASE_URL } from '../http/api-client.tokens';

export interface IClient {
  /**
   * Gets assessment context for an appointment when starting or resuming an assessment. Identifies if a cancelled
   * appointment has a newer scheduled appointment, or if a scheduled appointment has an older cancelled appointment.
   * Detects changes in NP, Intervention, or DateTime between appointments.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param appointmentId The appointment identifier to evaluate.
   * @returns OK
   */
  assessmentContext(clientId: number, appointmentId: number): Observable<AppointmentAssessmentContext>;
  /**
   * Gets the next upcoming appointment within a rolling time window. Returns the earliest appointment at or after
   * referenceTime minus windowMinutes. If a practitioner is currently with a patient (appointment started within the
   * window), that appointment remains visible until the next one is due. Returns 204 No Content when no appointment
   * falls within the window.
   *
   * @param referenceTime (optional) The reference date/time (UTC) from which to evaluate the window. Defaults to the
   *   current UTC time when not supplied.
   * @param windowMinutes (optional) Look-back window in minutes (must be >= 0). Default is 30 minutes.
   * @param includeInactive (optional) Whether to include inactive appointments.
   * @returns OK
   */
  next(
    referenceTime?: string | undefined,
    windowMinutes?: number | undefined,
    includeInactive?: boolean | undefined
  ): Observable<AppointmentInfoResponse>;
  /**
   * Gets all appointment card responses within an inclusive date range, sorted by appointment time ascending.
   *
   * @param from (optional) The start date (format: YYYY-MM-DD, UTC). Required.
   * @param to (optional) The end date (format: YYYY-MM-DD, UTC). Required. Must be on or after from.
   * @param includeInactive (optional) Whether to include inactive appointments.
   * @param excludeCancelled (optional) Whether to exclude cancelled appointments.
   * @param excludeArchived (optional) Whether to exclude appointments whose only active assessment is archived or
   *   deleted.
   * @returns OK
   */
  range(
    from?: string | undefined,
    to?: string | undefined,
    includeInactive?: boolean | undefined,
    excludeCancelled?: boolean | undefined,
    excludeArchived?: boolean | undefined
  ): Observable<AppointmentInfoResponse[]>;
  /**
   * Archives an assessment by updating its status to "Archived" and recording the archival reason. Optionally saves
   * the assessment before archiving when ShouldSaveAssessment=true and QuestionnaireResponseId is provided. This
   * allows permission-based control: if the user has permission to save (indicated by ShouldSaveAssessment flag), the
   * assessment is updated with the questionnaire response and Documented status before being archived.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param body (optional) The archive assessment request data, including optional QuestionnaireResponseId and
   *   ShouldSaveAssessment flag.
   * @returns OK
   */
  archive(clientId: number, body?: ArchiveAssessmentRequest | undefined): Observable<void>;
  /**
   * Soft-deletes an assessment by setting IsActive=false and updating its status to "Deleted". Used for the
   * appointment-scenario flows (start-fresh, carry-forward source, cancelled appointment) where no reason capture or
   * permission is required.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The assessment identifier to delete.
   * @returns OK
   */
  delete(clientId: number, assessmentId: number): Observable<void>;
  /**
   * Carries forward assessment data from one appointment to another. Creates a new assessment for the target
   * appointment if one doesn't exist. System-archives the source assessment. The frontend is responsible for carrying
   * forward FHIR QuestionnaireResponse data.
   *
   * @param clientId The client ID.
   * @param body (optional) The carry forward assessment request containing source and target appointment IDs.
   * @returns OK
   */
  carryForward(
    clientId: number,
    body?: CarryForwardAssessmentRequest | undefined
  ): Observable<CarryForwardAssessmentResponse>;
  /**
   * Streams an assessment PDF for inline viewing without exposing a blob URL.
   *
   * @param assessmentVersion (optional)
   * @param memberName (optional)
   * @returns OK
   */
  view(
    clientId: number,
    assessmentId: number,
    documentType: string,
    assessmentVersion?: number | undefined,
    memberName?: string | undefined
  ): Observable<FileResponse>;
  /**
   * Gets the validated PDF metadata (one per document type, latest version) available for a single assessment. Only
   * PDFs whose stored client-member identifier matches the assessment's member are returned.
   *
   * @param clientId Client identifier to resolve the target database.
   * @param assessmentId Assessment identifier.
   * @returns Returns the list of validated PDF metadata (possibly empty).
   */
  pdfs(clientId: number, assessmentId: number): Observable<AssessmentPdfMetadata[]>;
  /**
   * Returns the highest submitted assessment version number recorded for the assessment. The frontend compares this
   * against each generated PDF's `VersionNumber` to detect a stale PDF left over from before an in-progress
   * amendment.
   *
   * @param clientId Client identifier to resolve the target database.
   * @param assessmentId Assessment identifier.
   * @returns OK
   */
  latestVersion(clientId: number, assessmentId: number): Observable<AssessmentVersionSummaryResponse>;
  /**
   * Gets all assessments scheduled for a specific date, sorted by appointment time.
   *
   * @param assessmentDate (optional) Optional date (format: YYYY-MM-DD) for filtering assessments. Defaults to today
   *   in the requesting user's timezone if not provided.
   * @param includeInactive (optional) Whether to include inactive assessments.
   * @returns OK
   */
  assessments(
    assessmentDate?: string | undefined,
    includeInactive?: boolean | undefined
  ): Observable<AssessmentCardResponse[]>;
  /**
   * Gets an assessment by ID.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param id The assessment identifier.
   * @returns OK
   */
  client(clientId: number, id: number): Observable<AssessmentResponse>;
  /**
   * Gets assessments by client and member.
   *
   * @param clientId The client identifier.
   * @param memberId The member identifier.
   * @param includeInactive (optional) Whether to include inactive assessments.
   * @returns OK
   */
  member(clientId: number, memberId: number, includeInactive?: boolean | undefined): Observable<AssessmentResponse[]>;
  /**
   * Gets assessments by appointment.
   *
   * @param appointmentId The appointment identifier.
   * @returns OK
   */
  appointmentAll(appointmentId: number): Observable<AssessmentResponse[]>;
  /**
   * Gets assessments by template.
   *
   * @param templateId The template identifier.
   * @returns OK
   */
  template(templateId: number): Observable<AssessmentResponse[]>;
  /**
   * Gets assessments by status.
   *
   * @param statusId The assessment status identifier.
   * @returns OK
   */
  status(statusId: number): Observable<AssessmentResponse[]>;
  /**
   * Retrieves all global variables available for assessments.
   *
   * @returns OK
   */
  globalVariables(): Observable<GlobalVariableResponse[]>;
  /**
   * Retrieves the details of the published questionnaire associated with the specified assessment.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The unique identifier of the assessment for which to retrieve the published questionnaire
   *   details.
   * @returns OK
   */
  publishedQuestionnaire(clientId: number, assessmentId: number): Observable<AssessmentTemplateDetailsResponse>;
  /**
   * Gets the verification status for a given assessment.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The assessment ID.
   * @returns OK
   */
  verificationStatus(clientId: number, assessmentId: number): Observable<AssessmentVerificationDetailsResponse>;
  /**
   * Saves an assessment (creates if new, updates if existing).
   *
   * @param body (optional) The assessment data to save.
   * @returns OK
   */
  saveAssessment(body?: AssessmentResponse | undefined): Observable<number>;
  /**
   * Updates the published questionnaire associated with a specific assessment.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The unique identifier of the assessment for which the published questionnaire is to be
   *   updated.
   * @param publishedQuestionnaireId The unique identifier of the published questionnaire to associate with the
   *   assessment. Cannot be an empty GUID.
   * @returns OK
   */
  updatePublishedQuestionnaire(
    clientId: number,
    assessmentId: number,
    publishedQuestionnaireId: string
  ): Observable<void>;
  /**
   * Resets an assessment to "Not Started" status, clearing all questionnaire responses and related data. Only
   * assessments with status "In Progress" can be reset.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The assessment identifier to reset.
   * @returns OK
   */
  reset(clientId: number, assessmentId: number): Observable<void>;
  /**
   * Updates the questionnaire response associated with a specific assessment.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The unique identifier of the assessment for which the questionnaire response is to be
   *   updated.
   * @param body (optional) The request containing the updated assessment data.
   * @returns OK
   */
  updateAssessment(
    clientId: number,
    assessmentId: number,
    body?: AssessmentUpdateRequest | undefined
  ): Observable<void>;
  /**
   * Updates the verification data for an assessment including geolocation, verification checkboxes, and encounter
   * date. This endpoint performs a partial update to avoid overwriting other assessment fields.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The unique identifier of the assessment to update.
   * @param body (optional) The verification data to update.
   * @returns OK
   */
  verification(
    clientId: number,
    assessmentId: number,
    body?: UpdateAssessmentVerificationRequest | undefined
  ): Observable<void>;
  /**
   * Gets the previous version's QuestionnaireResponseId for an assessment. Used for amendment workflows to retrieve
   * the prior version's response for comparison or re-submission. Returns the baseline Assessment's
   * QuestionnaireResponseId if this is the first amendment (v1), or the prior AssessmentVersion's
   * QuestionnaireResponseId for subsequent amendments (v2+).
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The assessment identifier to retrieve the previous response for.
   * @returns OK
   */
  previousQuestionnaireResponse(clientId: number, assessmentId: number): Observable<string | null>;
  /**
   * Retrieves the details of an assessment template for the specified client, line of business (LOB), and
   * intervention type.
   *
   * @param clientId (optional) The client identifier used to resolve the target database.
   * @param lobId (optional) The identifier of the line of business (LOB) associated with the assessment template.
   * @param interventionTypeId (optional) The identifier of the intervention type associated with the assessment
   *   template.
   * @returns OK
   */
  getTemplateDetails(
    clientId?: number | undefined,
    lobId?: number | undefined,
    interventionTypeId?: number | undefined
  ): Observable<AssessmentTemplateDetailsResponse>;
  /**
   * Gets CEDI data for a specific assessment.
   *
   * @param clientId The client identifier that owns this assessment. Required to route the query to the correct
   *   database.
   * @param memberAppointmentId The assessment identifier.
   * @returns OK
   */
  cedi(clientId: number, memberAppointmentId: number): Observable<MemberCediResponse>;
  /**
   * Gets claims data for a specific assessment.
   *
   * @param clientId The client identifier that owns this assessment. Required to route the query to the correct
   *   database.
   * @param memberAppointmentId The assessment identifier.
   * @returns OK
   */
  claims(clientId: number, memberAppointmentId: number): Observable<MemberClaimsResponse>;
  /**
   * Gets the full dashboard profile for the currently authenticated nurse practitioner, including name, NPI, active
   * licenses, total patient count, and years in practice.
   *
   * @returns OK
   */
  profile(): Observable<NursePractitionerProfileResponse>;
  /**
   * Gets the overdue assessments for the current dashboard context.
   *
   * @param limit (optional) The maximum number of results to return. Optional, defaults to 5 if not provided.
   * @returns OK
   */
  overdueAssessments(limit?: number | undefined): Observable<OverdueAssessmentResponse[]>;
  /** @returns OK */
  health(): Observable<void>;
  /** @returns OK */
  detailed(): Observable<HealthCheckResponse>;
  /**
   * Gets medications data for a specific assessment.
   *
   * @param clientId The client identifier that owns this assessment. Required to route the query to the correct
   *   database.
   * @param memberAppointmentId The member appointment identifier.
   * @returns OK
   */
  medications(clientId: number, memberAppointmentId: number): Observable<MemberMedicationsResponse>;
  /**
   * Gets member information by client and appointment, with optional assessment PCP data enrichment.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param appointmentId The appointment identifier.
   * @param assessmentId (optional) The assessment identifier (optional - if provided, enriches with assessment PCP
   *   data).
   * @returns OK
   */
  appointment(
    clientId: number,
    appointmentId: number,
    assessmentId?: number | undefined
  ): Observable<MemberInfoResponse>;
  /**
   * Gets clinical summary data (medications, chronic conditions, gaps, claims) for a member appointment. Split out
   * from
   * M:Assessment.Api.Controllers.MemberController.GetMemberInfoByClientAndAppointment(System.Int32,System.Int64,System.Int64,System.Threading.CancellationToken)
   * so that this slower query can be fetched by the client in parallel, without blocking the rest of the member info
   * page from rendering.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param appointmentId The appointment identifier.
   * @returns OK
   */
  clinicalSummary(clientId: number, appointmentId: number): Observable<ClinicalSummaryResponse>;
  /**
   * Gets the active AssessmentMemberPCP for an assessment.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param assessmentId The assessment identifier.
   * @returns OK
   */
  assessment(clientId: number, assessmentId: number): Observable<AssessmentMemberPCPResponse>;
  /**
   * Searches for providers globally based on search criteria.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param body (optional) The search request containing filters and pagination parameters.
   * @returns OK
   */
  search(clientId: number, body?: ProviderSearchRequest | undefined): Observable<ProviderSearchResultPagedResult>;
  /**
   * Updates the Primary Care Physician for an assessment. Marks previous assessment PCP records as deleted and
   * inserts a new record.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param assessmentId The assessment identifier.
   * @param body (optional) The update request containing assessment PCP information.
   * @returns No Content
   */
  update(clientId: number, assessmentId: number, body?: UpdateAssessmentMemberPCPRequest | undefined): Observable<void>;
  /**
   * Gets paginated appointments for the currently authenticated practitioner with search and sort capabilities. The
   * practitioner is automatically identified from the `userId` JWT claim (UserManagementUserId).
   *
   *     For vendor/practitioner users (with VendorIds), appointments are retrieved across all active client databases
   *     filtered by the user's VendorIds. For client users (without VendorIds), results are restricted to their own client.
   *
   * @param body (optional) The pagination request with search and sort parameters.
   * @returns OK
   */
  appointments(body?: AppointmentPagedRequest | undefined): Observable<PractitionerAppointmentResponsePagedResult>;
  /**
   * Searches active Nurse Practitioners by name or email for the appointments filter autocomplete. Results are scoped
   * to the caller's accessible vendors.
   *
   * @param searchText (optional) Free-text search matched against the NP's first name, last name, and email.
   * @returns OK
   */
  searchAll(searchText?: string | undefined): Observable<NursePractitionerOptionResponse[]>;
  /**
   * @param loadType (optional)
   * @returns OK
   */
  dataLoad(
    clientId: number,
    appointmentId: number,
    loadType?: MemberDataLoadType | undefined
  ): Observable<DataLoadTriggerResponse>;
  /**
   * Returns the latest data load status for appointments already shown in the grid, checking Azure Data Factory for
   * runs that were not checked within the configured interval.
   *
   * @param body (optional) The appointments (client and appointment identifiers) to refresh.
   * @returns OK
   */
  dataLoadStatus(body?: DataLoadStatusRequest | undefined): Observable<DataLoadStatusResponse[]>;
  /**
   * Gets User Clinical Criteria (UCC) data for a specific assessment.
   *
   * @param clientId The client identifier that owns this assessment. Required to route the query to the correct
   *   database.
   * @param memberAppointmentId The assessment identifier.
   * @returns OK
   */
  ucc(clientId: number, memberAppointmentId: number): Observable<MemberUCCResponse>;
  /**
   * Gets the current weather conditions for a latitude and longitude.
   *
   * @returns Returns the current weather conditions.
   */
  currentConditions(latitude: number, longitude: number): Observable<WeatherCurrentConditionsResponse>;
}

@Injectable()
export class Client implements IClient {
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  private http = inject(HttpClient);
  private baseUrl = inject<string>(ASSESSMENT_API_BASE_URL);

  /**
   * Gets assessment context for an appointment when starting or resuming an assessment. Identifies if a cancelled
   * appointment has a newer scheduled appointment, or if a scheduled appointment has an older cancelled appointment.
   * Detects changes in NP, Intervention, or DateTime between appointments.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param appointmentId The appointment identifier to evaluate.
   * @returns OK
   */
  assessmentContext(clientId: number, appointmentId: number): Observable<AppointmentAssessmentContext> {
    let url_ = this.baseUrl + '/api/Member/client/{clientId}/appointments/{appointmentId}/assessment-context';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (appointmentId === undefined || appointmentId === null)
      throw new globalThis.Error("The parameter 'appointmentId' must be defined.");
    url_ = url_.replace('{appointmentId}', encodeURIComponent('' + appointmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAssessmentContext(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAssessmentContext(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AppointmentAssessmentContext>;
            }
          } else return _observableThrow(response_) as any as Observable<AppointmentAssessmentContext>;
        })
      );
  }

  protected processAssessmentContext(response: HttpResponseBase): Observable<AppointmentAssessmentContext> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AppointmentAssessmentContext.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets the next upcoming appointment within a rolling time window. Returns the earliest appointment at or after
   * referenceTime minus windowMinutes. If a practitioner is currently with a patient (appointment started within the
   * window), that appointment remains visible until the next one is due. Returns 204 No Content when no appointment
   * falls within the window.
   *
   * @param referenceTime (optional) The reference date/time (UTC) from which to evaluate the window. Defaults to the
   *   current UTC time when not supplied.
   * @param windowMinutes (optional) Look-back window in minutes (must be >= 0). Default is 30 minutes.
   * @param includeInactive (optional) Whether to include inactive appointments.
   * @returns OK
   */
  next(
    referenceTime?: string | undefined,
    windowMinutes?: number | undefined,
    includeInactive?: boolean | undefined
  ): Observable<AppointmentInfoResponse> {
    let url_ = this.baseUrl + '/api/Assessment/appointments/next?';
    if (referenceTime === null) throw new globalThis.Error("The parameter 'referenceTime' cannot be null.");
    else if (referenceTime !== undefined) url_ += 'referenceTime=' + encodeURIComponent('' + referenceTime) + '&';
    if (windowMinutes === null) throw new globalThis.Error("The parameter 'windowMinutes' cannot be null.");
    else if (windowMinutes !== undefined) url_ += 'windowMinutes=' + encodeURIComponent('' + windowMinutes) + '&';
    if (includeInactive === null) throw new globalThis.Error("The parameter 'includeInactive' cannot be null.");
    else if (includeInactive !== undefined) url_ += 'includeInactive=' + encodeURIComponent('' + includeInactive) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processNext(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processNext(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AppointmentInfoResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AppointmentInfoResponse>;
        })
      );
  }

  protected processNext(response: HttpResponseBase): Observable<AppointmentInfoResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AppointmentInfoResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets all appointment card responses within an inclusive date range, sorted by appointment time ascending.
   *
   * @param from (optional) The start date (format: YYYY-MM-DD, UTC). Required.
   * @param to (optional) The end date (format: YYYY-MM-DD, UTC). Required. Must be on or after from.
   * @param includeInactive (optional) Whether to include inactive appointments.
   * @param excludeCancelled (optional) Whether to exclude cancelled appointments.
   * @param excludeArchived (optional) Whether to exclude appointments whose only active assessment is archived or
   *   deleted.
   * @returns OK
   */
  range(
    from?: string | undefined,
    to?: string | undefined,
    includeInactive?: boolean | undefined,
    excludeCancelled?: boolean | undefined,
    excludeArchived?: boolean | undefined
  ): Observable<AppointmentInfoResponse[]> {
    let url_ = this.baseUrl + '/api/Assessment/appointments/range?';
    if (from === null) throw new globalThis.Error("The parameter 'from' cannot be null.");
    else if (from !== undefined) url_ += 'from=' + encodeURIComponent('' + from) + '&';
    if (to === null) throw new globalThis.Error("The parameter 'to' cannot be null.");
    else if (to !== undefined) url_ += 'to=' + encodeURIComponent('' + to) + '&';
    if (includeInactive === null) throw new globalThis.Error("The parameter 'includeInactive' cannot be null.");
    else if (includeInactive !== undefined) url_ += 'includeInactive=' + encodeURIComponent('' + includeInactive) + '&';
    if (excludeCancelled === null) throw new globalThis.Error("The parameter 'excludeCancelled' cannot be null.");
    else if (excludeCancelled !== undefined)
      url_ += 'excludeCancelled=' + encodeURIComponent('' + excludeCancelled) + '&';
    if (excludeArchived === null) throw new globalThis.Error("The parameter 'excludeArchived' cannot be null.");
    else if (excludeArchived !== undefined) url_ += 'excludeArchived=' + encodeURIComponent('' + excludeArchived) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processRange(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processRange(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AppointmentInfoResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<AppointmentInfoResponse[]>;
        })
      );
  }

  protected processRange(response: HttpResponseBase): Observable<AppointmentInfoResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(AppointmentInfoResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Archives an assessment by updating its status to "Archived" and recording the archival reason. Optionally saves
   * the assessment before archiving when ShouldSaveAssessment=true and QuestionnaireResponseId is provided. This
   * allows permission-based control: if the user has permission to save (indicated by ShouldSaveAssessment flag), the
   * assessment is updated with the questionnaire response and Documented status before being archived.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param body (optional) The archive assessment request data, including optional QuestionnaireResponseId and
   *   ShouldSaveAssessment flag.
   * @returns OK
   */
  archive(clientId: number, body?: ArchiveAssessmentRequest | undefined): Observable<void> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/archive';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .request('put', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processArchive(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processArchive(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processArchive(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Soft-deletes an assessment by setting IsActive=false and updating its status to "Deleted". Used for the
   * appointment-scenario flows (start-fresh, carry-forward source, cancelled appointment) where no reason capture or
   * permission is required.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The assessment identifier to delete.
   * @returns OK
   */
  delete(clientId: number, assessmentId: number): Observable<void> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{assessmentId}/delete';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({}),
    };

    return this.http
      .request('put', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDelete(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDelete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Carries forward assessment data from one appointment to another. Creates a new assessment for the target
   * appointment if one doesn't exist. System-archives the source assessment. The frontend is responsible for carrying
   * forward FHIR QuestionnaireResponse data.
   *
   * @param clientId The client ID.
   * @param body (optional) The carry forward assessment request containing source and target appointment IDs.
   * @returns OK
   */
  carryForward(
    clientId: number,
    body?: CarryForwardAssessmentRequest | undefined
  ): Observable<CarryForwardAssessmentResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/carry-forward';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCarryForward(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCarryForward(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CarryForwardAssessmentResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CarryForwardAssessmentResponse>;
        })
      );
  }

  protected processCarryForward(response: HttpResponseBase): Observable<CarryForwardAssessmentResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CarryForwardAssessmentResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Streams an assessment PDF for inline viewing without exposing a blob URL.
   *
   * @param assessmentVersion (optional)
   * @param memberName (optional)
   * @returns OK
   */
  view(
    clientId: number,
    assessmentId: number,
    documentType: string,
    assessmentVersion?: number | undefined,
    memberName?: string | undefined
  ): Observable<FileResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{assessmentId}/pdf/{documentType}/view?';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    if (documentType === undefined || documentType === null)
      throw new globalThis.Error("The parameter 'documentType' must be defined.");
    url_ = url_.replace('{documentType}', encodeURIComponent('' + documentType));
    if (assessmentVersion === null) throw new globalThis.Error("The parameter 'assessmentVersion' cannot be null.");
    else if (assessmentVersion !== undefined)
      url_ += 'assessmentVersion=' + encodeURIComponent('' + assessmentVersion) + '&';
    if (memberName === null) throw new globalThis.Error("The parameter 'memberName' cannot be null.");
    else if (memberName !== undefined) url_ += 'memberName=' + encodeURIComponent('' + memberName) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processView(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processView(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<FileResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<FileResponse>;
        })
      );
  }

  protected processView(response: HttpResponseBase): Observable<FileResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers ? response.headers.get('content-disposition') : undefined;
      let fileNameMatch = contentDisposition
        ? /filename\*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/g.exec(contentDisposition)
        : undefined;
      let fileName = fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[3] || fileNameMatch[2] : undefined;
      if (fileName) {
        fileName = decodeURIComponent(fileName);
      } else {
        fileNameMatch = contentDisposition ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition) : undefined;
        fileName = fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[1] : undefined;
      }
      return _observableOf({ fileName: fileName, data: responseBlob as any, status: status, headers: _headers });
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets the validated PDF metadata (one per document type, latest version) available for a single assessment. Only
   * PDFs whose stored client-member identifier matches the assessment's member are returned.
   *
   * @param clientId Client identifier to resolve the target database.
   * @param assessmentId Assessment identifier.
   * @returns Returns the list of validated PDF metadata (possibly empty).
   */
  pdfs(clientId: number, assessmentId: number): Observable<AssessmentPdfMetadata[]> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{assessmentId}/pdfs';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processPdfs(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processPdfs(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentPdfMetadata[]>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentPdfMetadata[]>;
        })
      );
  }

  protected processPdfs(response: HttpResponseBase): Observable<AssessmentPdfMetadata[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(AssessmentPdfMetadata.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Invalid request parameters.', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Returns the highest submitted assessment version number recorded for the assessment. The frontend compares this
   * against each generated PDF's `VersionNumber` to detect a stale PDF left over from before an in-progress
   * amendment.
   *
   * @param clientId Client identifier to resolve the target database.
   * @param assessmentId Assessment identifier.
   * @returns OK
   */
  latestVersion(clientId: number, assessmentId: number): Observable<AssessmentVersionSummaryResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{assessmentId}/latest-version';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processLatestVersion(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processLatestVersion(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentVersionSummaryResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentVersionSummaryResponse>;
        })
      );
  }

  protected processLatestVersion(response: HttpResponseBase): Observable<AssessmentVersionSummaryResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AssessmentVersionSummaryResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets all assessments scheduled for a specific date, sorted by appointment time.
   *
   * @param assessmentDate (optional) Optional date (format: YYYY-MM-DD) for filtering assessments. Defaults to today
   *   in the requesting user's timezone if not provided.
   * @param includeInactive (optional) Whether to include inactive assessments.
   * @returns OK
   */
  assessments(
    assessmentDate?: string | undefined,
    includeInactive?: boolean | undefined
  ): Observable<AssessmentCardResponse[]> {
    let url_ = this.baseUrl + '/api/Assessment/assessments?';
    if (assessmentDate === null) throw new globalThis.Error("The parameter 'assessmentDate' cannot be null.");
    else if (assessmentDate !== undefined) url_ += 'assessmentDate=' + encodeURIComponent('' + assessmentDate) + '&';
    if (includeInactive === null) throw new globalThis.Error("The parameter 'includeInactive' cannot be null.");
    else if (includeInactive !== undefined) url_ += 'includeInactive=' + encodeURIComponent('' + includeInactive) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAssessments(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAssessments(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentCardResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentCardResponse[]>;
        })
      );
  }

  protected processAssessments(response: HttpResponseBase): Observable<AssessmentCardResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(AssessmentCardResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets an assessment by ID.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param id The assessment identifier.
   * @returns OK
   */
  client(clientId: number, id: number): Observable<AssessmentResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{id}';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (id === undefined || id === null) throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace('{id}', encodeURIComponent('' + id));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processClient(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processClient(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentResponse>;
        })
      );
  }

  protected processClient(response: HttpResponseBase): Observable<AssessmentResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AssessmentResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets assessments by client and member.
   *
   * @param clientId The client identifier.
   * @param memberId The member identifier.
   * @param includeInactive (optional) Whether to include inactive assessments.
   * @returns OK
   */
  member(clientId: number, memberId: number, includeInactive?: boolean | undefined): Observable<AssessmentResponse[]> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/member/{memberId}?';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (memberId === undefined || memberId === null)
      throw new globalThis.Error("The parameter 'memberId' must be defined.");
    url_ = url_.replace('{memberId}', encodeURIComponent('' + memberId));
    if (includeInactive === null) throw new globalThis.Error("The parameter 'includeInactive' cannot be null.");
    else if (includeInactive !== undefined) url_ += 'includeInactive=' + encodeURIComponent('' + includeInactive) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processMember(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processMember(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentResponse[]>;
        })
      );
  }

  protected processMember(response: HttpResponseBase): Observable<AssessmentResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(AssessmentResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets assessments by appointment.
   *
   * @param appointmentId The appointment identifier.
   * @returns OK
   */
  appointmentAll(appointmentId: number): Observable<AssessmentResponse[]> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/appointment/{appointmentId}';
    if (appointmentId === undefined || appointmentId === null)
      throw new globalThis.Error("The parameter 'appointmentId' must be defined.");
    url_ = url_.replace('{appointmentId}', encodeURIComponent('' + appointmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAppointmentAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAppointmentAll(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentResponse[]>;
        })
      );
  }

  protected processAppointmentAll(response: HttpResponseBase): Observable<AssessmentResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(AssessmentResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets assessments by template.
   *
   * @param templateId The template identifier.
   * @returns OK
   */
  template(templateId: number): Observable<AssessmentResponse[]> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/template/{templateId}';
    if (templateId === undefined || templateId === null)
      throw new globalThis.Error("The parameter 'templateId' must be defined.");
    url_ = url_.replace('{templateId}', encodeURIComponent('' + templateId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processTemplate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processTemplate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentResponse[]>;
        })
      );
  }

  protected processTemplate(response: HttpResponseBase): Observable<AssessmentResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(AssessmentResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets assessments by status.
   *
   * @param statusId The assessment status identifier.
   * @returns OK
   */
  status(statusId: number): Observable<AssessmentResponse[]> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/status/{statusId}';
    if (statusId === undefined || statusId === null)
      throw new globalThis.Error("The parameter 'statusId' must be defined.");
    url_ = url_.replace('{statusId}', encodeURIComponent('' + statusId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processStatus(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processStatus(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentResponse[]>;
        })
      );
  }

  protected processStatus(response: HttpResponseBase): Observable<AssessmentResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(AssessmentResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Retrieves all global variables available for assessments.
   *
   * @returns OK
   */
  globalVariables(): Observable<GlobalVariableResponse[]> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/global-variables';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGlobalVariables(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGlobalVariables(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<GlobalVariableResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<GlobalVariableResponse[]>;
        })
      );
  }

  protected processGlobalVariables(response: HttpResponseBase): Observable<GlobalVariableResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(GlobalVariableResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Retrieves the details of the published questionnaire associated with the specified assessment.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The unique identifier of the assessment for which to retrieve the published questionnaire
   *   details.
   * @returns OK
   */
  publishedQuestionnaire(clientId: number, assessmentId: number): Observable<AssessmentTemplateDetailsResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/published-questionnaire/{assessmentId}';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processPublishedQuestionnaire(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processPublishedQuestionnaire(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentTemplateDetailsResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentTemplateDetailsResponse>;
        })
      );
  }

  protected processPublishedQuestionnaire(response: HttpResponseBase): Observable<AssessmentTemplateDetailsResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AssessmentTemplateDetailsResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets the verification status for a given assessment.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The assessment ID.
   * @returns OK
   */
  verificationStatus(clientId: number, assessmentId: number): Observable<AssessmentVerificationDetailsResponse> {
    let url_ = this.baseUrl + '/api/Assessment/client/{clientId}/{assessmentId}/verification-status';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processVerificationStatus(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processVerificationStatus(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentVerificationDetailsResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentVerificationDetailsResponse>;
        })
      );
  }

  protected processVerificationStatus(response: HttpResponseBase): Observable<AssessmentVerificationDetailsResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AssessmentVerificationDetailsResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Saves an assessment (creates if new, updates if existing).
   *
   * @param body (optional) The assessment data to save.
   * @returns OK
   */
  saveAssessment(body?: AssessmentResponse | undefined): Observable<number> {
    let url_ = this.baseUrl + '/api/Assessment/save-assessment';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSaveAssessment(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSaveAssessment(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<number>;
            }
          } else return _observableThrow(response_) as any as Observable<number>;
        })
      );
  }

  protected processSaveAssessment(response: HttpResponseBase): Observable<number> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : (null as any);

          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Updates the published questionnaire associated with a specific assessment.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The unique identifier of the assessment for which the published questionnaire is to be
   *   updated.
   * @param publishedQuestionnaireId The unique identifier of the published questionnaire to associate with the
   *   assessment. Cannot be an empty GUID.
   * @returns OK
   */
  updatePublishedQuestionnaire(
    clientId: number,
    assessmentId: number,
    publishedQuestionnaireId: string
  ): Observable<void> {
    let url_ =
      this.baseUrl +
      '/api/Assessment/assessments/client/{clientId}/update-published-questionnaire/{assessmentId}/{publishedQuestionnaireId}';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    if (publishedQuestionnaireId === undefined || publishedQuestionnaireId === null)
      throw new globalThis.Error("The parameter 'publishedQuestionnaireId' must be defined.");
    url_ = url_.replace('{publishedQuestionnaireId}', encodeURIComponent('' + publishedQuestionnaireId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({}),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdatePublishedQuestionnaire(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdatePublishedQuestionnaire(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processUpdatePublishedQuestionnaire(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Resets an assessment to "Not Started" status, clearing all questionnaire responses and related data. Only
   * assessments with status "In Progress" can be reset.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The assessment identifier to reset.
   * @returns OK
   */
  reset(clientId: number, assessmentId: number): Observable<void> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{assessmentId}/reset';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({}),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processReset(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processReset(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processReset(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Updates the questionnaire response associated with a specific assessment.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The unique identifier of the assessment for which the questionnaire response is to be
   *   updated.
   * @param body (optional) The request containing the updated assessment data.
   * @returns OK
   */
  updateAssessment(
    clientId: number,
    assessmentId: number,
    body?: AssessmentUpdateRequest | undefined
  ): Observable<void> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/update-assessment/{assessmentId}';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .request('put', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdateAssessment(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdateAssessment(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processUpdateAssessment(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Updates the verification data for an assessment including geolocation, verification checkboxes, and encounter
   * date. This endpoint performs a partial update to avoid overwriting other assessment fields.
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The unique identifier of the assessment to update.
   * @param body (optional) The verification data to update.
   * @returns OK
   */
  verification(
    clientId: number,
    assessmentId: number,
    body?: UpdateAssessmentVerificationRequest | undefined
  ): Observable<void> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{assessmentId}/verification';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .request('patch', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processVerification(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processVerification(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processVerification(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets the previous version's QuestionnaireResponseId for an assessment. Used for amendment workflows to retrieve
   * the prior version's response for comparison or re-submission. Returns the baseline Assessment's
   * QuestionnaireResponseId if this is the first amendment (v1), or the prior AssessmentVersion's
   * QuestionnaireResponseId for subsequent amendments (v2+).
   *
   * @param clientId The client identifier used to resolve the target database.
   * @param assessmentId The assessment identifier to retrieve the previous response for.
   * @returns OK
   */
  previousQuestionnaireResponse(clientId: number, assessmentId: number): Observable<string | null> {
    let url_ =
      this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{assessmentId}/previous-questionnaire-response';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processPreviousQuestionnaireResponse(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processPreviousQuestionnaireResponse(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<string | null>;
            }
          } else return _observableThrow(response_) as any as Observable<string | null>;
        })
      );
  }

  protected processPreviousQuestionnaireResponse(response: HttpResponseBase): Observable<string | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : (null as any);

          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Retrieves the details of an assessment template for the specified client, line of business (LOB), and
   * intervention type.
   *
   * @param clientId (optional) The client identifier used to resolve the target database.
   * @param lobId (optional) The identifier of the line of business (LOB) associated with the assessment template.
   * @param interventionTypeId (optional) The identifier of the intervention type associated with the assessment
   *   template.
   * @returns OK
   */
  getTemplateDetails(
    clientId?: number | undefined,
    lobId?: number | undefined,
    interventionTypeId?: number | undefined
  ): Observable<AssessmentTemplateDetailsResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/get-template-details?';
    if (clientId === null) throw new globalThis.Error("The parameter 'clientId' cannot be null.");
    else if (clientId !== undefined) url_ += 'clientId=' + encodeURIComponent('' + clientId) + '&';
    if (lobId === null) throw new globalThis.Error("The parameter 'lobId' cannot be null.");
    else if (lobId !== undefined) url_ += 'lobId=' + encodeURIComponent('' + lobId) + '&';
    if (interventionTypeId === null) throw new globalThis.Error("The parameter 'interventionTypeId' cannot be null.");
    else if (interventionTypeId !== undefined)
      url_ += 'interventionTypeId=' + encodeURIComponent('' + interventionTypeId) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetTemplateDetails(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetTemplateDetails(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentTemplateDetailsResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentTemplateDetailsResponse>;
        })
      );
  }

  protected processGetTemplateDetails(response: HttpResponseBase): Observable<AssessmentTemplateDetailsResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AssessmentTemplateDetailsResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets CEDI data for a specific assessment.
   *
   * @param clientId The client identifier that owns this assessment. Required to route the query to the correct
   *   database.
   * @param memberAppointmentId The assessment identifier.
   * @returns OK
   */
  cedi(clientId: number, memberAppointmentId: number): Observable<MemberCediResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{MemberAppointmentId}/cedi';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (memberAppointmentId === undefined || memberAppointmentId === null)
      throw new globalThis.Error("The parameter 'memberAppointmentId' must be defined.");
    url_ = url_.replace('{memberAppointmentId}', encodeURIComponent('' + memberAppointmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCedi(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCedi(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MemberCediResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<MemberCediResponse>;
        })
      );
  }

  protected processCedi(response: HttpResponseBase): Observable<MemberCediResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MemberCediResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets claims data for a specific assessment.
   *
   * @param clientId The client identifier that owns this assessment. Required to route the query to the correct
   *   database.
   * @param memberAppointmentId The assessment identifier.
   * @returns OK
   */
  claims(clientId: number, memberAppointmentId: number): Observable<MemberClaimsResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{MemberAppointmentId}/claims';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (memberAppointmentId === undefined || memberAppointmentId === null)
      throw new globalThis.Error("The parameter 'memberAppointmentId' must be defined.");
    url_ = url_.replace('{memberAppointmentId}', encodeURIComponent('' + memberAppointmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processClaims(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processClaims(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MemberClaimsResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<MemberClaimsResponse>;
        })
      );
  }

  protected processClaims(response: HttpResponseBase): Observable<MemberClaimsResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MemberClaimsResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets the full dashboard profile for the currently authenticated nurse practitioner, including name, NPI, active
   * licenses, total patient count, and years in practice.
   *
   * @returns OK
   */
  profile(): Observable<NursePractitionerProfileResponse> {
    let url_ = this.baseUrl + '/api/Dashboard/profile';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processProfile(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processProfile(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<NursePractitionerProfileResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<NursePractitionerProfileResponse>;
        })
      );
  }

  protected processProfile(response: HttpResponseBase): Observable<NursePractitionerProfileResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = NursePractitionerProfileResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets the overdue assessments for the current dashboard context.
   *
   * @param limit (optional) The maximum number of results to return. Optional, defaults to 5 if not provided.
   * @returns OK
   */
  overdueAssessments(limit?: number | undefined): Observable<OverdueAssessmentResponse[]> {
    let url_ = this.baseUrl + '/api/Dashboard/overdue-assessments?';
    if (limit === null) throw new globalThis.Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined) url_ += 'limit=' + encodeURIComponent('' + limit) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processOverdueAssessments(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processOverdueAssessments(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<OverdueAssessmentResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<OverdueAssessmentResponse[]>;
        })
      );
  }

  protected processOverdueAssessments(response: HttpResponseBase): Observable<OverdueAssessmentResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(OverdueAssessmentResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /** @returns OK */
  health(): Observable<void> {
    let url_ = this.baseUrl + '/health';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({}),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processHealth(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processHealth(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processHealth(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status === 503) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Service Unavailable', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /** @returns OK */
  detailed(): Observable<HealthCheckResponse> {
    let url_ = this.baseUrl + '/health/detailed';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDetailed(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDetailed(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<HealthCheckResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<HealthCheckResponse>;
        })
      );
  }

  protected processDetailed(response: HttpResponseBase): Observable<HealthCheckResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = HealthCheckResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status === 503) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result503: any = null;
          let resultData503 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result503 = HealthCheckResponse.fromJS(resultData503);
          return throwException('Service Unavailable', status, _responseText, _headers, result503);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets medications data for a specific assessment.
   *
   * @param clientId The client identifier that owns this assessment. Required to route the query to the correct
   *   database.
   * @param memberAppointmentId The member appointment identifier.
   * @returns OK
   */
  medications(clientId: number, memberAppointmentId: number): Observable<MemberMedicationsResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{MemberAppointmentId}/medications';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (memberAppointmentId === undefined || memberAppointmentId === null)
      throw new globalThis.Error("The parameter 'memberAppointmentId' must be defined.");
    url_ = url_.replace('{memberAppointmentId}', encodeURIComponent('' + memberAppointmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processMedications(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processMedications(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MemberMedicationsResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<MemberMedicationsResponse>;
        })
      );
  }

  protected processMedications(response: HttpResponseBase): Observable<MemberMedicationsResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MemberMedicationsResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets member information by client and appointment, with optional assessment PCP data enrichment.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param appointmentId The appointment identifier.
   * @param assessmentId (optional) The assessment identifier (optional - if provided, enriches with assessment PCP
   *   data).
   * @returns OK
   */
  appointment(
    clientId: number,
    appointmentId: number,
    assessmentId?: number | undefined
  ): Observable<MemberInfoResponse> {
    let url_ = this.baseUrl + '/api/Member/info/client/{clientId}/appointment/{appointmentId}?';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (appointmentId === undefined || appointmentId === null)
      throw new globalThis.Error("The parameter 'appointmentId' must be defined.");
    url_ = url_.replace('{appointmentId}', encodeURIComponent('' + appointmentId));
    if (assessmentId === null) throw new globalThis.Error("The parameter 'assessmentId' cannot be null.");
    else if (assessmentId !== undefined) url_ += 'assessmentId=' + encodeURIComponent('' + assessmentId) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAppointment(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAppointment(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MemberInfoResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<MemberInfoResponse>;
        })
      );
  }

  protected processAppointment(response: HttpResponseBase): Observable<MemberInfoResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MemberInfoResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets clinical summary data (medications, chronic conditions, gaps, claims) for a member appointment. Split out
   * from
   * M:Assessment.Api.Controllers.MemberController.GetMemberInfoByClientAndAppointment(System.Int32,System.Int64,System.Int64,System.Threading.CancellationToken)
   * so that this slower query can be fetched by the client in parallel, without blocking the rest of the member info
   * page from rendering.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param appointmentId The appointment identifier.
   * @returns OK
   */
  clinicalSummary(clientId: number, appointmentId: number): Observable<ClinicalSummaryResponse> {
    let url_ = this.baseUrl + '/api/Member/info/client/{clientId}/appointment/{appointmentId}/clinical-summary';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (appointmentId === undefined || appointmentId === null)
      throw new globalThis.Error("The parameter 'appointmentId' must be defined.");
    url_ = url_.replace('{appointmentId}', encodeURIComponent('' + appointmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processClinicalSummary(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processClinicalSummary(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ClinicalSummaryResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<ClinicalSummaryResponse>;
        })
      );
  }

  protected processClinicalSummary(response: HttpResponseBase): Observable<ClinicalSummaryResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ClinicalSummaryResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets the active AssessmentMemberPCP for an assessment.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param assessmentId The assessment identifier.
   * @returns OK
   */
  assessment(clientId: number, assessmentId: number): Observable<AssessmentMemberPCPResponse> {
    let url_ = this.baseUrl + '/api/PCP/client/{clientId}/assessment/{assessmentId}';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAssessment(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAssessment(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AssessmentMemberPCPResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AssessmentMemberPCPResponse>;
        })
      );
  }

  protected processAssessment(response: HttpResponseBase): Observable<AssessmentMemberPCPResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AssessmentMemberPCPResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Searches for providers globally based on search criteria.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param body (optional) The search request containing filters and pagination parameters.
   * @returns OK
   */
  search(clientId: number, body?: ProviderSearchRequest | undefined): Observable<ProviderSearchResultPagedResult> {
    let url_ = this.baseUrl + '/api/PCP/client/{clientId}/search';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSearch(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSearch(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ProviderSearchResultPagedResult>;
            }
          } else return _observableThrow(response_) as any as Observable<ProviderSearchResultPagedResult>;
        })
      );
  }

  protected processSearch(response: HttpResponseBase): Observable<ProviderSearchResultPagedResult> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ProviderSearchResultPagedResult.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Updates the Primary Care Physician for an assessment. Marks previous assessment PCP records as deleted and
   * inserts a new record.
   *
   * @param clientId The client identifier (required — used to resolve the client database).
   * @param assessmentId The assessment identifier.
   * @param body (optional) The update request containing assessment PCP information.
   * @returns No Content
   */
  update(
    clientId: number,
    assessmentId: number,
    body?: UpdateAssessmentMemberPCPRequest | undefined
  ): Observable<void> {
    let url_ = this.baseUrl + '/api/PCP/client/{clientId}/assessment/{assessmentId}/update';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (assessmentId === undefined || assessmentId === null)
      throw new globalThis.Error("The parameter 'assessmentId' must be defined.");
    url_ = url_.replace('{assessmentId}', encodeURIComponent('' + assessmentId));
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processUpdate(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 201) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          // 201 Created is a success status - return success, not exception
          return _observableOf(null as any);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets paginated appointments for the currently authenticated practitioner with search and sort capabilities. The
   * practitioner is automatically identified from the `userId` JWT claim (UserManagementUserId).
   *
   *     For vendor/practitioner users (with VendorIds), appointments are retrieved across all active client databases
   *     filtered by the user's VendorIds. For client users (without VendorIds), results are restricted to their own client.
   *
   * @param body (optional) The pagination request with search and sort parameters.
   * @returns OK
   */
  appointments(body?: AppointmentPagedRequest | undefined): Observable<PractitionerAppointmentResponsePagedResult> {
    let url_ = this.baseUrl + '/api/Member/practitioner/appointments';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAppointments(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAppointments(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<PractitionerAppointmentResponsePagedResult>;
            }
          } else return _observableThrow(response_) as any as Observable<PractitionerAppointmentResponsePagedResult>;
        })
      );
  }

  protected processAppointments(response: HttpResponseBase): Observable<PractitionerAppointmentResponsePagedResult> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = PractitionerAppointmentResponsePagedResult.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Searches active Nurse Practitioners by name or email for the appointments filter autocomplete. Results are scoped
   * to the caller's accessible vendors.
   *
   * @param searchText (optional) Free-text search matched against the NP's first name, last name, and email.
   * @returns OK
   */
  searchAll(searchText?: string | undefined): Observable<NursePractitionerOptionResponse[]> {
    let url_ = this.baseUrl + '/api/Member/nurse-practitioners/search?';
    if (searchText === null) throw new globalThis.Error("The parameter 'searchText' cannot be null.");
    else if (searchText !== undefined) url_ += 'searchText=' + encodeURIComponent('' + searchText) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSearchAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSearchAll(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<NursePractitionerOptionResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<NursePractitionerOptionResponse[]>;
        })
      );
  }

  protected processSearchAll(response: HttpResponseBase): Observable<NursePractitionerOptionResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(NursePractitionerOptionResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param loadType (optional)
   * @returns OK
   */
  dataLoad(
    clientId: number,
    appointmentId: number,
    loadType?: MemberDataLoadType | undefined
  ): Observable<DataLoadTriggerResponse> {
    let url_ = this.baseUrl + '/api/Member/client/{clientId}/appointments/{appointmentId}/data-load?';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (appointmentId === undefined || appointmentId === null)
      throw new globalThis.Error("The parameter 'appointmentId' must be defined.");
    url_ = url_.replace('{appointmentId}', encodeURIComponent('' + appointmentId));
    if (loadType === null) throw new globalThis.Error("The parameter 'loadType' cannot be null.");
    else if (loadType !== undefined) url_ += 'loadType=' + encodeURIComponent('' + loadType) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDataLoad(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDataLoad(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<DataLoadTriggerResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<DataLoadTriggerResponse>;
        })
      );
  }

  protected processDataLoad(response: HttpResponseBase): Observable<DataLoadTriggerResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DataLoadTriggerResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status === 502) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Bad Gateway', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Returns the latest data load status for appointments already shown in the grid, checking Azure Data Factory for
   * runs that were not checked within the configured interval.
   *
   * @param body (optional) The appointments (client and appointment identifiers) to refresh.
   * @returns OK
   */
  dataLoadStatus(body?: DataLoadStatusRequest | undefined): Observable<DataLoadStatusResponse[]> {
    let url_ = this.baseUrl + '/api/Member/practitioner/appointments/data-load-status';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDataLoadStatus(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDataLoadStatus(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<DataLoadStatusResponse[]>;
            }
          } else return _observableThrow(response_) as any as Observable<DataLoadStatusResponse[]>;
        })
      );
  }

  protected processDataLoadStatus(response: HttpResponseBase): Observable<DataLoadStatusResponse[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(DataLoadStatusResponse.fromJS(item));
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets User Clinical Criteria (UCC) data for a specific assessment.
   *
   * @param clientId The client identifier that owns this assessment. Required to route the query to the correct
   *   database.
   * @param memberAppointmentId The assessment identifier.
   * @returns OK
   */
  ucc(clientId: number, memberAppointmentId: number): Observable<MemberUCCResponse> {
    let url_ = this.baseUrl + '/api/Assessment/assessments/client/{clientId}/{MemberAppointmentId}/ucc';
    if (clientId === undefined || clientId === null)
      throw new globalThis.Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace('{clientId}', encodeURIComponent('' + clientId));
    if (memberAppointmentId === undefined || memberAppointmentId === null)
      throw new globalThis.Error("The parameter 'memberAppointmentId' must be defined.");
    url_ = url_.replace('{memberAppointmentId}', encodeURIComponent('' + memberAppointmentId));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUcc(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUcc(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<MemberUCCResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<MemberUCCResponse>;
        })
      );
  }

  protected processUcc(response: HttpResponseBase): Observable<MemberUCCResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MemberUCCResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * Gets the current weather conditions for a latitude and longitude.
   *
   * @returns Returns the current weather conditions.
   */
  currentConditions(latitude: number, longitude: number): Observable<WeatherCurrentConditionsResponse> {
    let url_ = this.baseUrl + '/api/Weather/current-conditions?';
    if (latitude === undefined || latitude === null)
      throw new globalThis.Error("The parameter 'latitude' must be defined and cannot be null.");
    else url_ += 'Latitude=' + encodeURIComponent('' + latitude) + '&';
    if (longitude === undefined || longitude === null)
      throw new globalThis.Error("The parameter 'longitude' must be defined and cannot be null.");
    else url_ += 'Longitude=' + encodeURIComponent('' + longitude) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCurrentConditions(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCurrentConditions(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<WeatherCurrentConditionsResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<WeatherCurrentConditionsResponse>;
        })
      );
  }

  protected processCurrentConditions(response: HttpResponseBase): Observable<WeatherCurrentConditionsResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = WeatherCurrentConditionsResponse.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        })
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('If the coordinates are invalid.', status, _responseText, _headers, result400);
        })
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        })
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        })
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException(
            'If no weather data is available for the coordinates.',
            status,
            _responseText,
            _headers,
            result404
          );
        })
      );
    } else if (status === 409) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result409: any = null;
          let resultData409 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result409 = ProblemDetails.fromJS(resultData409);
          return throwException('Conflict', status, _responseText, _headers, result409);
        })
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('Internal Server Error', status, _responseText, _headers);
        })
      );
    } else if (status === 502) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('If the external weather provider fails.', status, _responseText, _headers);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        })
      );
    }
    return _observableOf(null as any);
  }
}

export class AppointmentAssessmentContext implements IAppointmentAssessmentContext {
  currentAppointmentId!: number;
  relatedAppointmentId?: number | undefined;
  scenario!: AppointmentScenario;
  hasAssessmentInProgress!: boolean;
  changeType!: AppointmentChangeType;
  readonly hasRelatedAppointment?: boolean;

  constructor(data?: IAppointmentAssessmentContext) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.currentAppointmentId = _data['currentAppointmentId'];
      this.relatedAppointmentId = _data['relatedAppointmentId'];
      this.scenario = _data['scenario'];
      this.hasAssessmentInProgress = _data['hasAssessmentInProgress'];
      this.changeType = _data['changeType'];
      (this as any).hasRelatedAppointment = _data['hasRelatedAppointment'];
    }
  }

  static fromJS(data: any): AppointmentAssessmentContext {
    data = typeof data === 'object' ? data : {};
    let result = new AppointmentAssessmentContext();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['currentAppointmentId'] = this.currentAppointmentId;
    data['relatedAppointmentId'] = this.relatedAppointmentId;
    data['scenario'] = this.scenario;
    data['hasAssessmentInProgress'] = this.hasAssessmentInProgress;
    data['changeType'] = this.changeType;
    data['hasRelatedAppointment'] = this.hasRelatedAppointment;
    return data;
  }
}

export interface IAppointmentAssessmentContext {
  currentAppointmentId: number;
  relatedAppointmentId?: number | undefined;
  scenario: AppointmentScenario;
  hasAssessmentInProgress: boolean;
  changeType: AppointmentChangeType;
  hasRelatedAppointment?: boolean;
}

export enum AppointmentChangeType {
  _0 = 0,
  _1 = 1,
  _2 = 2,
  _3 = 3,
}

export class AppointmentInfoResponse implements IAppointmentInfoResponse {
  memberId?: number;
  clientMemberId?: string | undefined;
  memberFirstName?: string | undefined;
  memberLastName?: string | undefined;
  birthDay?: string | undefined;
  gender?: string | undefined;
  lobId?: number;
  planType?: string | undefined;
  appointmentId?: number | undefined;
  appointmentConfirmationId?: string | undefined;
  appointmentDateTime?: string;
  appointmentStatus?: string | undefined;
  appointmentStatusId?: number | undefined;
  meetingUrl?: string | undefined;
  clientId?: number;
  clientName?: string | undefined;
  vendorId?: number;
  interventionTypeId?: number;
  interventionType?: string | undefined;
  assessmentId?: number | undefined;
  assessmentTypeId?: number | undefined;
  assessmentStatusId?: number;
  templateId?: number;
  questionnaireResponseId?: string | undefined;
  address?: string | undefined;
  mailingAddress?: string | undefined;
  appointmentAddress1?: string | undefined;
  appointmentAddress2?: string | undefined;
  appointmentCity?: string | undefined;
  appointmentState?: string | undefined;
  appointmentZip?: string | undefined;
  weather?: string | undefined;
  availableGaps?: string[] | undefined;
  mailedGaps?: string[] | undefined;
  canStartAssessment?: boolean;
  canStartAssessmentReason?: string | undefined;
  incentiveAmount?: number | undefined;
  incentiveType?: number | undefined;

  constructor(data?: IAppointmentInfoResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.memberId = _data['memberId'];
      this.clientMemberId = _data['clientMemberId'];
      this.memberFirstName = _data['memberFirstName'];
      this.memberLastName = _data['memberLastName'];
      this.birthDay = _data['birthDay'];
      this.gender = _data['gender'];
      this.lobId = _data['lobId'];
      this.planType = _data['planType'];
      this.appointmentId = _data['appointmentId'];
      this.appointmentConfirmationId = _data['appointmentConfirmationId'];
      this.appointmentDateTime = _data['appointmentDateTime'];
      this.appointmentStatus = _data['appointmentStatus'];
      this.appointmentStatusId = _data['appointmentStatusId'];
      this.meetingUrl = _data['meetingUrl'];
      this.clientId = _data['clientId'];
      this.clientName = _data['clientName'];
      this.vendorId = _data['vendorId'];
      this.interventionTypeId = _data['interventionTypeId'];
      this.interventionType = _data['interventionType'];
      this.assessmentId = _data['assessmentId'];
      this.assessmentTypeId = _data['assessmentTypeId'];
      this.assessmentStatusId = _data['assessmentStatusId'];
      this.templateId = _data['templateId'];
      this.questionnaireResponseId = _data['questionnaireResponseId'];
      this.address = _data['address'];
      this.mailingAddress = _data['mailingAddress'];
      this.appointmentAddress1 = _data['appointmentAddress1'];
      this.appointmentAddress2 = _data['appointmentAddress2'];
      this.appointmentCity = _data['appointmentCity'];
      this.appointmentState = _data['appointmentState'];
      this.appointmentZip = _data['appointmentZip'];
      this.weather = _data['weather'];
      if (Array.isArray(_data['availableGaps'])) {
        this.availableGaps = [] as any;
        for (let item of _data['availableGaps']) this.availableGaps!.push(item);
      }
      if (Array.isArray(_data['mailedGaps'])) {
        this.mailedGaps = [] as any;
        for (let item of _data['mailedGaps']) this.mailedGaps!.push(item);
      }
      this.canStartAssessment = _data['canStartAssessment'];
      this.canStartAssessmentReason = _data['canStartAssessmentReason'];
      this.incentiveAmount = _data['incentiveAmount'];
      this.incentiveType = _data['incentiveType'];
    }
  }

  static fromJS(data: any): AppointmentInfoResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AppointmentInfoResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['memberId'] = this.memberId;
    data['clientMemberId'] = this.clientMemberId;
    data['memberFirstName'] = this.memberFirstName;
    data['memberLastName'] = this.memberLastName;
    data['birthDay'] = this.birthDay;
    data['gender'] = this.gender;
    data['lobId'] = this.lobId;
    data['planType'] = this.planType;
    data['appointmentId'] = this.appointmentId;
    data['appointmentConfirmationId'] = this.appointmentConfirmationId;
    data['appointmentDateTime'] = this.appointmentDateTime;
    data['appointmentStatus'] = this.appointmentStatus;
    data['appointmentStatusId'] = this.appointmentStatusId;
    data['meetingUrl'] = this.meetingUrl;
    data['clientId'] = this.clientId;
    data['clientName'] = this.clientName;
    data['vendorId'] = this.vendorId;
    data['interventionTypeId'] = this.interventionTypeId;
    data['interventionType'] = this.interventionType;
    data['assessmentId'] = this.assessmentId;
    data['assessmentTypeId'] = this.assessmentTypeId;
    data['assessmentStatusId'] = this.assessmentStatusId;
    data['templateId'] = this.templateId;
    data['questionnaireResponseId'] = this.questionnaireResponseId;
    data['address'] = this.address;
    data['mailingAddress'] = this.mailingAddress;
    data['appointmentAddress1'] = this.appointmentAddress1;
    data['appointmentAddress2'] = this.appointmentAddress2;
    data['appointmentCity'] = this.appointmentCity;
    data['appointmentState'] = this.appointmentState;
    data['appointmentZip'] = this.appointmentZip;
    data['weather'] = this.weather;
    if (Array.isArray(this.availableGaps)) {
      data['availableGaps'] = [];
      for (let item of this.availableGaps) data['availableGaps'].push(item);
    }
    if (Array.isArray(this.mailedGaps)) {
      data['mailedGaps'] = [];
      for (let item of this.mailedGaps) data['mailedGaps'].push(item);
    }
    data['canStartAssessment'] = this.canStartAssessment;
    data['canStartAssessmentReason'] = this.canStartAssessmentReason;
    data['incentiveAmount'] = this.incentiveAmount;
    data['incentiveType'] = this.incentiveType;
    return data;
  }
}

export interface IAppointmentInfoResponse {
  memberId?: number;
  clientMemberId?: string | undefined;
  memberFirstName?: string | undefined;
  memberLastName?: string | undefined;
  birthDay?: string | undefined;
  gender?: string | undefined;
  lobId?: number;
  planType?: string | undefined;
  appointmentId?: number | undefined;
  appointmentConfirmationId?: string | undefined;
  appointmentDateTime?: string;
  appointmentStatus?: string | undefined;
  appointmentStatusId?: number | undefined;
  meetingUrl?: string | undefined;
  clientId?: number;
  clientName?: string | undefined;
  vendorId?: number;
  interventionTypeId?: number;
  interventionType?: string | undefined;
  assessmentId?: number | undefined;
  assessmentTypeId?: number | undefined;
  assessmentStatusId?: number;
  templateId?: number;
  questionnaireResponseId?: string | undefined;
  address?: string | undefined;
  mailingAddress?: string | undefined;
  appointmentAddress1?: string | undefined;
  appointmentAddress2?: string | undefined;
  appointmentCity?: string | undefined;
  appointmentState?: string | undefined;
  appointmentZip?: string | undefined;
  weather?: string | undefined;
  availableGaps?: string[] | undefined;
  mailedGaps?: string[] | undefined;
  canStartAssessment?: boolean;
  canStartAssessmentReason?: string | undefined;
  incentiveAmount?: number | undefined;
  incentiveType?: number | undefined;
}

export class AppointmentPagedRequest implements IAppointmentPagedRequest {
  page?: number;
  pageSize?: number;
  readonly skip?: number;
  searchSort?: SearchSortParams;
  appointmentStatusIds?: number[] | undefined;
  assessmentStatusIds?: number[] | undefined;
  interventionTypeIds?: number[] | undefined;
  lobIds?: number[] | undefined;
  appointmentDateFrom?: string | undefined;
  appointmentDateTo?: string | undefined;
  vendorIds?: number[] | undefined;
  clientIds?: number[] | undefined;
  npName?: string | undefined;
  npUserId?: number | undefined;
  npi?: string | undefined;
  memberName?: string | undefined;
  memberId?: string | undefined;
  clientMemberId?: string | undefined;

  constructor(data?: IAppointmentPagedRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.page = _data['page'];
      this.pageSize = _data['pageSize'];
      (this as any).skip = _data['skip'];
      this.searchSort = _data['searchSort'] ? SearchSortParams.fromJS(_data['searchSort']) : (undefined as any);
      if (Array.isArray(_data['appointmentStatusIds'])) {
        this.appointmentStatusIds = [] as any;
        for (let item of _data['appointmentStatusIds']) this.appointmentStatusIds!.push(item);
      }
      if (Array.isArray(_data['assessmentStatusIds'])) {
        this.assessmentStatusIds = [] as any;
        for (let item of _data['assessmentStatusIds']) this.assessmentStatusIds!.push(item);
      }
      if (Array.isArray(_data['interventionTypeIds'])) {
        this.interventionTypeIds = [] as any;
        for (let item of _data['interventionTypeIds']) this.interventionTypeIds!.push(item);
      }
      if (Array.isArray(_data['lobIds'])) {
        this.lobIds = [] as any;
        for (let item of _data['lobIds']) this.lobIds!.push(item);
      }
      this.appointmentDateFrom = _data['appointmentDateFrom'];
      this.appointmentDateTo = _data['appointmentDateTo'];
      if (Array.isArray(_data['vendorIds'])) {
        this.vendorIds = [] as any;
        for (let item of _data['vendorIds']) this.vendorIds!.push(item);
      }
      if (Array.isArray(_data['clientIds'])) {
        this.clientIds = [] as any;
        for (let item of _data['clientIds']) this.clientIds!.push(item);
      }
      this.npName = _data['npName'];
      this.npUserId = _data['npUserId'];
      this.npi = _data['npi'];
      this.memberName = _data['memberName'];
      this.memberId = _data['memberId'];
      this.clientMemberId = _data['clientMemberId'];
    }
  }

  static fromJS(data: any): AppointmentPagedRequest {
    data = typeof data === 'object' ? data : {};
    let result = new AppointmentPagedRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['page'] = this.page;
    data['pageSize'] = this.pageSize;
    data['skip'] = this.skip;
    data['searchSort'] = this.searchSort ? this.searchSort.toJSON() : (undefined as any);
    if (Array.isArray(this.appointmentStatusIds)) {
      data['appointmentStatusIds'] = [];
      for (let item of this.appointmentStatusIds) data['appointmentStatusIds'].push(item);
    }
    if (Array.isArray(this.assessmentStatusIds)) {
      data['assessmentStatusIds'] = [];
      for (let item of this.assessmentStatusIds) data['assessmentStatusIds'].push(item);
    }
    if (Array.isArray(this.interventionTypeIds)) {
      data['interventionTypeIds'] = [];
      for (let item of this.interventionTypeIds) data['interventionTypeIds'].push(item);
    }
    if (Array.isArray(this.lobIds)) {
      data['lobIds'] = [];
      for (let item of this.lobIds) data['lobIds'].push(item);
    }
    data['appointmentDateFrom'] = this.appointmentDateFrom;
    data['appointmentDateTo'] = this.appointmentDateTo;
    if (Array.isArray(this.vendorIds)) {
      data['vendorIds'] = [];
      for (let item of this.vendorIds) data['vendorIds'].push(item);
    }
    if (Array.isArray(this.clientIds)) {
      data['clientIds'] = [];
      for (let item of this.clientIds) data['clientIds'].push(item);
    }
    data['npName'] = this.npName;
    data['npUserId'] = this.npUserId;
    data['npi'] = this.npi;
    data['memberName'] = this.memberName;
    data['memberId'] = this.memberId;
    data['clientMemberId'] = this.clientMemberId;
    return data;
  }
}

export interface IAppointmentPagedRequest {
  page?: number;
  pageSize?: number;
  skip?: number;
  searchSort?: SearchSortParams;
  appointmentStatusIds?: number[] | undefined;
  assessmentStatusIds?: number[] | undefined;
  interventionTypeIds?: number[] | undefined;
  lobIds?: number[] | undefined;
  appointmentDateFrom?: string | undefined;
  appointmentDateTo?: string | undefined;
  vendorIds?: number[] | undefined;
  clientIds?: number[] | undefined;
  npName?: string | undefined;
  npUserId?: number | undefined;
  npi?: string | undefined;
  memberName?: string | undefined;
  memberId?: string | undefined;
  clientMemberId?: string | undefined;
}

export enum AppointmentScenario {
  _0 = 0,
  _1 = 1,
  _2 = 2,
  _3 = 3,
}

export class ArchiveAssessmentRequest implements IArchiveAssessmentRequest {
  assessmentId?: number;
  archivalReason?: number;
  archivalOtherReason?: string | undefined;
  questionnaireResponseId?: string | undefined;
  shouldSaveAssessment?: boolean;

  constructor(data?: IArchiveAssessmentRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.assessmentId = _data['assessmentId'];
      this.archivalReason = _data['archivalReason'];
      this.archivalOtherReason = _data['archivalOtherReason'];
      this.questionnaireResponseId = _data['questionnaireResponseId'];
      this.shouldSaveAssessment = _data['shouldSaveAssessment'];
    }
  }

  static fromJS(data: any): ArchiveAssessmentRequest {
    data = typeof data === 'object' ? data : {};
    let result = new ArchiveAssessmentRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['assessmentId'] = this.assessmentId;
    data['archivalReason'] = this.archivalReason;
    data['archivalOtherReason'] = this.archivalOtherReason;
    data['questionnaireResponseId'] = this.questionnaireResponseId;
    data['shouldSaveAssessment'] = this.shouldSaveAssessment;
    return data;
  }
}

export interface IArchiveAssessmentRequest {
  assessmentId?: number;
  archivalReason?: number;
  archivalOtherReason?: string | undefined;
  questionnaireResponseId?: string | undefined;
  shouldSaveAssessment?: boolean;
}

export class AssessmentCardResponse implements IAssessmentCardResponse {
  memberId?: number;
  memberFirstName?: string | undefined;
  memberLastName?: string | undefined;
  birthDay?: string | undefined;
  gender?: string | undefined;
  lobId?: number;
  planType?: string | undefined;
  appointmentDateTime?: string;
  medicalHistory?: string[] | undefined;
  assessmentDetails?: AssessmentDetails;
  assessmentId?: number | undefined;
  assessmentTypeId?: number | undefined;
  assessmentStatusId?: number;
  templateId?: number;
  clientId?: number;
  clientName?: string | undefined;
  vendorId?: number;
  interventionTypeId?: number;
  interventionType?: string | undefined;
  appointmentId?: number | undefined;
  appointmentConfirmationId?: string | undefined;
  userId?: number | undefined;
  createdDate?: string;
  modifiedDate?: string;
  questionnaireResponseId?: string | undefined;

  constructor(data?: IAssessmentCardResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.memberId = _data['memberId'];
      this.memberFirstName = _data['memberFirstName'];
      this.memberLastName = _data['memberLastName'];
      this.birthDay = _data['birthDay'];
      this.gender = _data['gender'];
      this.lobId = _data['lobId'];
      this.planType = _data['planType'];
      this.appointmentDateTime = _data['appointmentDateTime'];
      if (Array.isArray(_data['medicalHistory'])) {
        this.medicalHistory = [] as any;
        for (let item of _data['medicalHistory']) this.medicalHistory!.push(item);
      }
      this.assessmentDetails = _data['assessmentDetails']
        ? AssessmentDetails.fromJS(_data['assessmentDetails'])
        : (undefined as any);
      this.assessmentId = _data['assessmentId'];
      this.assessmentTypeId = _data['assessmentTypeId'];
      this.assessmentStatusId = _data['assessmentStatusId'];
      this.templateId = _data['templateId'];
      this.clientId = _data['clientId'];
      this.clientName = _data['clientName'];
      this.vendorId = _data['vendorId'];
      this.interventionTypeId = _data['interventionTypeId'];
      this.interventionType = _data['interventionType'];
      this.appointmentId = _data['appointmentId'];
      this.appointmentConfirmationId = _data['appointmentConfirmationId'];
      this.userId = _data['userId'];
      this.createdDate = _data['createdDate'];
      this.modifiedDate = _data['modifiedDate'];
      this.questionnaireResponseId = _data['questionnaireResponseId'];
    }
  }

  static fromJS(data: any): AssessmentCardResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentCardResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['memberId'] = this.memberId;
    data['memberFirstName'] = this.memberFirstName;
    data['memberLastName'] = this.memberLastName;
    data['birthDay'] = this.birthDay;
    data['gender'] = this.gender;
    data['lobId'] = this.lobId;
    data['planType'] = this.planType;
    data['appointmentDateTime'] = this.appointmentDateTime;
    if (Array.isArray(this.medicalHistory)) {
      data['medicalHistory'] = [];
      for (let item of this.medicalHistory) data['medicalHistory'].push(item);
    }
    data['assessmentDetails'] = this.assessmentDetails ? this.assessmentDetails.toJSON() : (undefined as any);
    data['assessmentId'] = this.assessmentId;
    data['assessmentTypeId'] = this.assessmentTypeId;
    data['assessmentStatusId'] = this.assessmentStatusId;
    data['templateId'] = this.templateId;
    data['clientId'] = this.clientId;
    data['clientName'] = this.clientName;
    data['vendorId'] = this.vendorId;
    data['interventionTypeId'] = this.interventionTypeId;
    data['interventionType'] = this.interventionType;
    data['appointmentId'] = this.appointmentId;
    data['appointmentConfirmationId'] = this.appointmentConfirmationId;
    data['userId'] = this.userId;
    data['createdDate'] = this.createdDate;
    data['modifiedDate'] = this.modifiedDate;
    data['questionnaireResponseId'] = this.questionnaireResponseId;
    return data;
  }
}

export interface IAssessmentCardResponse {
  memberId?: number;
  memberFirstName?: string | undefined;
  memberLastName?: string | undefined;
  birthDay?: string | undefined;
  gender?: string | undefined;
  lobId?: number;
  planType?: string | undefined;
  appointmentDateTime?: string;
  medicalHistory?: string[] | undefined;
  assessmentDetails?: AssessmentDetails;
  assessmentId?: number | undefined;
  assessmentTypeId?: number | undefined;
  assessmentStatusId?: number;
  templateId?: number;
  clientId?: number;
  clientName?: string | undefined;
  vendorId?: number;
  interventionTypeId?: number;
  interventionType?: string | undefined;
  appointmentId?: number | undefined;
  appointmentConfirmationId?: string | undefined;
  userId?: number | undefined;
  createdDate?: string;
  modifiedDate?: string;
  questionnaireResponseId?: string | undefined;
}

export class AssessmentDetails implements IAssessmentDetails {
  assessmentAddress?: string | undefined;
  distance?: number | undefined;
  time?: number | undefined;
  weather?: string | undefined;

  constructor(data?: IAssessmentDetails) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.assessmentAddress = _data['assessmentAddress'];
      this.distance = _data['distance'];
      this.time = _data['time'];
      this.weather = _data['weather'];
    }
  }

  static fromJS(data: any): AssessmentDetails {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentDetails();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['assessmentAddress'] = this.assessmentAddress;
    data['distance'] = this.distance;
    data['time'] = this.time;
    data['weather'] = this.weather;
    return data;
  }
}

export interface IAssessmentDetails {
  assessmentAddress?: string | undefined;
  distance?: number | undefined;
  time?: number | undefined;
  weather?: string | undefined;
}

export class AssessmentMemberPCPResponse implements IAssessmentMemberPCPResponse {
  id?: number;
  name?: string | undefined;
  npiNumber?: string | undefined;
  phoneNumber?: string | undefined;
  address?: string | undefined;
  state?: string | undefined;
  faxNumber?: string | undefined;
  effectiveDate?: string | undefined;

  constructor(data?: IAssessmentMemberPCPResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data['id'];
      this.name = _data['name'];
      this.npiNumber = _data['npiNumber'];
      this.phoneNumber = _data['phoneNumber'];
      this.address = _data['address'];
      this.state = _data['state'];
      this.faxNumber = _data['faxNumber'];
      this.effectiveDate = _data['effectiveDate'];
    }
  }

  static fromJS(data: any): AssessmentMemberPCPResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentMemberPCPResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['id'] = this.id;
    data['name'] = this.name;
    data['npiNumber'] = this.npiNumber;
    data['phoneNumber'] = this.phoneNumber;
    data['address'] = this.address;
    data['state'] = this.state;
    data['faxNumber'] = this.faxNumber;
    data['effectiveDate'] = this.effectiveDate;
    return data;
  }
}

export interface IAssessmentMemberPCPResponse {
  id?: number;
  name?: string | undefined;
  npiNumber?: string | undefined;
  phoneNumber?: string | undefined;
  address?: string | undefined;
  state?: string | undefined;
  faxNumber?: string | undefined;
  effectiveDate?: string | undefined;
}

export class AssessmentPdfMetadata implements IAssessmentPdfMetadata {
  assessmentPDFId?: number;
  documentType?: string | undefined;
  versionNumber?: number;
  fileName?: string | undefined;
  generatedAt?: string;

  constructor(data?: IAssessmentPdfMetadata) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.assessmentPDFId = _data['assessmentPDFId'];
      this.documentType = _data['documentType'];
      this.versionNumber = _data['versionNumber'];
      this.fileName = _data['fileName'];
      this.generatedAt = _data['generatedAt'];
    }
  }

  static fromJS(data: any): AssessmentPdfMetadata {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentPdfMetadata();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['assessmentPDFId'] = this.assessmentPDFId;
    data['documentType'] = this.documentType;
    data['versionNumber'] = this.versionNumber;
    data['fileName'] = this.fileName;
    data['generatedAt'] = this.generatedAt;
    return data;
  }
}

export interface IAssessmentPdfMetadata {
  assessmentPDFId?: number;
  documentType?: string | undefined;
  versionNumber?: number;
  fileName?: string | undefined;
  generatedAt?: string;
}

export class AssessmentResponse implements IAssessmentResponse {
  assessmentId?: number | undefined;
  assessmentTypeId?: number;
  assessmentStatusId?: number | undefined;
  templateId?: number;
  templateConfigurationRequestHistoryId?: number;
  templateVersionId?: number;
  clientId?: number;
  vendorId?: number;
  memberId?: number;
  appointmentId?: number | undefined;
  userId?: number | undefined;
  createdDate?: string;
  modifiedDate?: string;
  lastAmendedDate?: string | undefined;
  lobId?: number;
  interventionId?: number;
  questionnaireResponseId?: string | undefined;
  patient?: string | undefined;
  practitioner?: string | undefined;
  appointment?: string | undefined;
  isIdentityVerified?: boolean;
  pcpConfirmationStatusId?: number;
  isEncounterDateVerified?: boolean;
  encounterDate?: string | undefined;

  constructor(data?: IAssessmentResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.assessmentId = _data['assessmentId'];
      this.assessmentTypeId = _data['assessmentTypeId'];
      this.assessmentStatusId = _data['assessmentStatusId'];
      this.templateId = _data['templateId'];
      this.templateConfigurationRequestHistoryId = _data['templateConfigurationRequestHistoryId'];
      this.templateVersionId = _data['templateVersionId'];
      this.clientId = _data['clientId'];
      this.vendorId = _data['vendorId'];
      this.memberId = _data['memberId'];
      this.appointmentId = _data['appointmentId'];
      this.userId = _data['userId'];
      this.createdDate = _data['createdDate'];
      this.modifiedDate = _data['modifiedDate'];
      this.lastAmendedDate = _data['lastAmendedDate'];
      this.lobId = _data['lobId'];
      this.interventionId = _data['interventionId'];
      this.questionnaireResponseId = _data['questionnaireResponseId'];
      this.patient = _data['patient'];
      this.practitioner = _data['practitioner'];
      this.appointment = _data['appointment'];
      this.isIdentityVerified = _data['isIdentityVerified'];
      this.pcpConfirmationStatusId = _data['pcpConfirmationStatusId'];
      this.isEncounterDateVerified = _data['isEncounterDateVerified'];
      this.encounterDate = _data['encounterDate'];
    }
  }

  static fromJS(data: any): AssessmentResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['assessmentId'] = this.assessmentId;
    data['assessmentTypeId'] = this.assessmentTypeId;
    data['assessmentStatusId'] = this.assessmentStatusId;
    data['templateId'] = this.templateId;
    data['templateConfigurationRequestHistoryId'] = this.templateConfigurationRequestHistoryId;
    data['templateVersionId'] = this.templateVersionId;
    data['clientId'] = this.clientId;
    data['vendorId'] = this.vendorId;
    data['memberId'] = this.memberId;
    data['appointmentId'] = this.appointmentId;
    data['userId'] = this.userId;
    data['createdDate'] = this.createdDate;
    data['modifiedDate'] = this.modifiedDate;
    data['lastAmendedDate'] = this.lastAmendedDate;
    data['lobId'] = this.lobId;
    data['interventionId'] = this.interventionId;
    data['questionnaireResponseId'] = this.questionnaireResponseId;
    data['patient'] = this.patient;
    data['practitioner'] = this.practitioner;
    data['appointment'] = this.appointment;
    data['isIdentityVerified'] = this.isIdentityVerified;
    data['pcpConfirmationStatusId'] = this.pcpConfirmationStatusId;
    data['isEncounterDateVerified'] = this.isEncounterDateVerified;
    data['encounterDate'] = this.encounterDate;
    return data;
  }
}

export interface IAssessmentResponse {
  assessmentId?: number | undefined;
  assessmentTypeId?: number;
  assessmentStatusId?: number | undefined;
  templateId?: number;
  templateConfigurationRequestHistoryId?: number;
  templateVersionId?: number;
  clientId?: number;
  vendorId?: number;
  memberId?: number;
  appointmentId?: number | undefined;
  userId?: number | undefined;
  createdDate?: string;
  modifiedDate?: string;
  lastAmendedDate?: string | undefined;
  lobId?: number;
  interventionId?: number;
  questionnaireResponseId?: string | undefined;
  patient?: string | undefined;
  practitioner?: string | undefined;
  appointment?: string | undefined;
  isIdentityVerified?: boolean;
  pcpConfirmationStatusId?: number;
  isEncounterDateVerified?: boolean;
  encounterDate?: string | undefined;
}

export class AssessmentTemplateDetailsResponse implements IAssessmentTemplateDetailsResponse {
  publishedQuestionnaireId?: string;
  assessmentStartTime?: string | undefined;
  assessmentStatus?: number;
  clientConfigJson?: string | undefined;
  templateId?: number;
  templateConfigurationRequestHistoryId?: number | undefined;
  templateVersionId?: number;

  constructor(data?: IAssessmentTemplateDetailsResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.publishedQuestionnaireId = _data['publishedQuestionnaireId'];
      this.assessmentStartTime = _data['assessmentStartTime'];
      this.assessmentStatus = _data['assessmentStatus'];
      this.clientConfigJson = _data['clientConfigJson'];
      this.templateId = _data['templateId'];
      this.templateConfigurationRequestHistoryId = _data['templateConfigurationRequestHistoryId'];
      this.templateVersionId = _data['templateVersionId'];
    }
  }

  static fromJS(data: any): AssessmentTemplateDetailsResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentTemplateDetailsResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['publishedQuestionnaireId'] = this.publishedQuestionnaireId;
    data['assessmentStartTime'] = this.assessmentStartTime;
    data['assessmentStatus'] = this.assessmentStatus;
    data['clientConfigJson'] = this.clientConfigJson;
    data['templateId'] = this.templateId;
    data['templateConfigurationRequestHistoryId'] = this.templateConfigurationRequestHistoryId;
    data['templateVersionId'] = this.templateVersionId;
    return data;
  }
}

export interface IAssessmentTemplateDetailsResponse {
  publishedQuestionnaireId?: string;
  assessmentStartTime?: string | undefined;
  assessmentStatus?: number;
  clientConfigJson?: string | undefined;
  templateId?: number;
  templateConfigurationRequestHistoryId?: number | undefined;
  templateVersionId?: number;
}

export class AssessmentUpdateRequest implements IAssessmentUpdateRequest {
  questionnaireResponseId?: string | undefined;
  assessmentStatusId?: number | undefined;
  patient?: string | undefined;
  practitioner?: string | undefined;
  appointment?: string | undefined;
  isSubmittedOffline?: boolean;
  submittedLocalDateTime?: string | undefined;
  submittedLatitude?: number | undefined;
  submittedLongitude?: number | undefined;
  startLatitude?: number | undefined;
  startLongitude?: number | undefined;
  submissionIntent?: SubmissionIntent;
  denormQuestionnaireResponseId?: string | undefined;

  constructor(data?: IAssessmentUpdateRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.questionnaireResponseId = _data['questionnaireResponseId'];
      this.assessmentStatusId = _data['assessmentStatusId'];
      this.patient = _data['patient'];
      this.practitioner = _data['practitioner'];
      this.appointment = _data['appointment'];
      this.isSubmittedOffline = _data['isSubmittedOffline'];
      this.submittedLocalDateTime = _data['submittedLocalDateTime'];
      this.submittedLatitude = _data['submittedLatitude'];
      this.submittedLongitude = _data['submittedLongitude'];
      this.startLatitude = _data['startLatitude'];
      this.startLongitude = _data['startLongitude'];
      this.submissionIntent = _data['submissionIntent'];
      this.denormQuestionnaireResponseId = _data['denormQuestionnaireResponseId'];
    }
  }

  static fromJS(data: any): AssessmentUpdateRequest {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentUpdateRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['questionnaireResponseId'] = this.questionnaireResponseId;
    data['assessmentStatusId'] = this.assessmentStatusId;
    data['patient'] = this.patient;
    data['practitioner'] = this.practitioner;
    data['appointment'] = this.appointment;
    data['isSubmittedOffline'] = this.isSubmittedOffline;
    data['submittedLocalDateTime'] = this.submittedLocalDateTime;
    data['submittedLatitude'] = this.submittedLatitude;
    data['submittedLongitude'] = this.submittedLongitude;
    data['startLatitude'] = this.startLatitude;
    data['startLongitude'] = this.startLongitude;
    data['submissionIntent'] = this.submissionIntent;
    data['denormQuestionnaireResponseId'] = this.denormQuestionnaireResponseId;
    return data;
  }
}

export interface IAssessmentUpdateRequest {
  questionnaireResponseId?: string | undefined;
  assessmentStatusId?: number | undefined;
  patient?: string | undefined;
  practitioner?: string | undefined;
  appointment?: string | undefined;
  isSubmittedOffline?: boolean;
  submittedLocalDateTime?: string | undefined;
  submittedLatitude?: number | undefined;
  submittedLongitude?: number | undefined;
  startLatitude?: number | undefined;
  startLongitude?: number | undefined;
  submissionIntent?: SubmissionIntent;
  denormQuestionnaireResponseId?: string | undefined;
}

export class AssessmentVerificationDetailsResponse implements IAssessmentVerificationDetailsResponse {
  startLatitude?: number | undefined;
  startLongitude?: number | undefined;
  isLocationConfirmed?: boolean | undefined;
  locationDeclineReasonId?: number | undefined;
  locationDeclineNotes?: string | undefined;
  isIdentityVerified?: boolean;
  pcpConfirmationStatusId?: number;
  isEncounterDateVerified?: boolean;
  mismatchReason?: string | undefined;
  encounterDate?: string | undefined;

  constructor(data?: IAssessmentVerificationDetailsResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.startLatitude = _data['startLatitude'];
      this.startLongitude = _data['startLongitude'];
      this.isLocationConfirmed = _data['isLocationConfirmed'];
      this.locationDeclineReasonId = _data['locationDeclineReasonId'];
      this.locationDeclineNotes = _data['locationDeclineNotes'];
      this.isIdentityVerified = _data['isIdentityVerified'];
      this.pcpConfirmationStatusId = _data['pcpConfirmationStatusId'];
      this.isEncounterDateVerified = _data['isEncounterDateVerified'];
      this.mismatchReason = _data['mismatchReason'];
      this.encounterDate = _data['encounterDate'];
    }
  }

  static fromJS(data: any): AssessmentVerificationDetailsResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentVerificationDetailsResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['startLatitude'] = this.startLatitude;
    data['startLongitude'] = this.startLongitude;
    data['isLocationConfirmed'] = this.isLocationConfirmed;
    data['locationDeclineReasonId'] = this.locationDeclineReasonId;
    data['locationDeclineNotes'] = this.locationDeclineNotes;
    data['isIdentityVerified'] = this.isIdentityVerified;
    data['pcpConfirmationStatusId'] = this.pcpConfirmationStatusId;
    data['isEncounterDateVerified'] = this.isEncounterDateVerified;
    data['mismatchReason'] = this.mismatchReason;
    data['encounterDate'] = this.encounterDate;
    return data;
  }
}

export interface IAssessmentVerificationDetailsResponse {
  startLatitude?: number | undefined;
  startLongitude?: number | undefined;
  isLocationConfirmed?: boolean | undefined;
  locationDeclineReasonId?: number | undefined;
  locationDeclineNotes?: string | undefined;
  isIdentityVerified?: boolean;
  pcpConfirmationStatusId?: number;
  isEncounterDateVerified?: boolean;
  mismatchReason?: string | undefined;
  encounterDate?: string | undefined;
}

export class AssessmentVersionSummaryResponse implements IAssessmentVersionSummaryResponse {
  versionNumber?: number;

  constructor(data?: IAssessmentVersionSummaryResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.versionNumber = _data['versionNumber'];
    }
  }

  static fromJS(data: any): AssessmentVersionSummaryResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AssessmentVersionSummaryResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['versionNumber'] = this.versionNumber;
    return data;
  }
}

export interface IAssessmentVersionSummaryResponse {
  versionNumber?: number;
}

export class CarryForwardAssessmentRequest implements ICarryForwardAssessmentRequest {
  sourceAppointmentId!: number;
  targetAppointmentId!: number;
  clonedQuestionnaireId!: string;
  clonedQuestionnaireResponseId!: string;

  constructor(data?: ICarryForwardAssessmentRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.sourceAppointmentId = _data['sourceAppointmentId'];
      this.targetAppointmentId = _data['targetAppointmentId'];
      this.clonedQuestionnaireId = _data['clonedQuestionnaireId'];
      this.clonedQuestionnaireResponseId = _data['clonedQuestionnaireResponseId'];
    }
  }

  static fromJS(data: any): CarryForwardAssessmentRequest {
    data = typeof data === 'object' ? data : {};
    let result = new CarryForwardAssessmentRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['sourceAppointmentId'] = this.sourceAppointmentId;
    data['targetAppointmentId'] = this.targetAppointmentId;
    data['clonedQuestionnaireId'] = this.clonedQuestionnaireId;
    data['clonedQuestionnaireResponseId'] = this.clonedQuestionnaireResponseId;
    return data;
  }
}

export interface ICarryForwardAssessmentRequest {
  sourceAppointmentId: number;
  targetAppointmentId: number;
  clonedQuestionnaireId: string;
  clonedQuestionnaireResponseId: string;
}

export class CarryForwardAssessmentResponse implements ICarryForwardAssessmentResponse {
  assessmentId!: number;
  clientId!: number;
  appointmentId!: number;
  memberId!: number;

  constructor(data?: ICarryForwardAssessmentResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.assessmentId = _data['assessmentId'];
      this.clientId = _data['clientId'];
      this.appointmentId = _data['appointmentId'];
      this.memberId = _data['memberId'];
    }
  }

  static fromJS(data: any): CarryForwardAssessmentResponse {
    data = typeof data === 'object' ? data : {};
    let result = new CarryForwardAssessmentResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['assessmentId'] = this.assessmentId;
    data['clientId'] = this.clientId;
    data['appointmentId'] = this.appointmentId;
    data['memberId'] = this.memberId;
    return data;
  }
}

export interface ICarryForwardAssessmentResponse {
  assessmentId: number;
  clientId: number;
  appointmentId: number;
  memberId: number;
}

export class ClinicalConditionResponse implements IClinicalConditionResponse {
  code?: string | undefined;
  name?: string | undefined;

  constructor(data?: IClinicalConditionResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.code = _data['code'];
      this.name = _data['name'];
    }
  }

  static fromJS(data: any): ClinicalConditionResponse {
    data = typeof data === 'object' ? data : {};
    let result = new ClinicalConditionResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['code'] = this.code;
    data['name'] = this.name;
    return data;
  }
}

export interface IClinicalConditionResponse {
  code?: string | undefined;
  name?: string | undefined;
}

export class ClinicalSummaryResponse implements IClinicalSummaryResponse {
  medications?: string[] | undefined;
  priorYearChronicConditions?: ClinicalConditionResponse[] | undefined;
  suspectedChronicConditions?: ClinicalConditionResponse[] | undefined;
  gaps?: ClinicalConditionResponse[] | undefined;
  claims?: ClinicalConditionResponse[] | undefined;
  hasMoreClaims?: boolean;

  constructor(data?: IClinicalSummaryResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['medications'])) {
        this.medications = [] as any;
        for (let item of _data['medications']) this.medications!.push(item);
      }
      if (Array.isArray(_data['priorYearChronicConditions'])) {
        this.priorYearChronicConditions = [] as any;
        for (let item of _data['priorYearChronicConditions'])
          this.priorYearChronicConditions!.push(ClinicalConditionResponse.fromJS(item));
      }
      if (Array.isArray(_data['suspectedChronicConditions'])) {
        this.suspectedChronicConditions = [] as any;
        for (let item of _data['suspectedChronicConditions'])
          this.suspectedChronicConditions!.push(ClinicalConditionResponse.fromJS(item));
      }
      if (Array.isArray(_data['gaps'])) {
        this.gaps = [] as any;
        for (let item of _data['gaps']) this.gaps!.push(ClinicalConditionResponse.fromJS(item));
      }
      if (Array.isArray(_data['claims'])) {
        this.claims = [] as any;
        for (let item of _data['claims']) this.claims!.push(ClinicalConditionResponse.fromJS(item));
      }
      this.hasMoreClaims = _data['hasMoreClaims'];
    }
  }

  static fromJS(data: any): ClinicalSummaryResponse {
    data = typeof data === 'object' ? data : {};
    let result = new ClinicalSummaryResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.medications)) {
      data['medications'] = [];
      for (let item of this.medications) data['medications'].push(item);
    }
    if (Array.isArray(this.priorYearChronicConditions)) {
      data['priorYearChronicConditions'] = [];
      for (let item of this.priorYearChronicConditions)
        data['priorYearChronicConditions'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.suspectedChronicConditions)) {
      data['suspectedChronicConditions'] = [];
      for (let item of this.suspectedChronicConditions)
        data['suspectedChronicConditions'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.gaps)) {
      data['gaps'] = [];
      for (let item of this.gaps) data['gaps'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.claims)) {
      data['claims'] = [];
      for (let item of this.claims) data['claims'].push(item ? item.toJSON() : (undefined as any));
    }
    data['hasMoreClaims'] = this.hasMoreClaims;
    return data;
  }
}

export interface IClinicalSummaryResponse {
  medications?: string[] | undefined;
  priorYearChronicConditions?: ClinicalConditionResponse[] | undefined;
  suspectedChronicConditions?: ClinicalConditionResponse[] | undefined;
  gaps?: ClinicalConditionResponse[] | undefined;
  claims?: ClinicalConditionResponse[] | undefined;
  hasMoreClaims?: boolean;
}

export class DataLoadStatusRequest implements IDataLoadStatusRequest {
  items?: DataLoadStatusRequestItem[] | undefined;

  constructor(data?: IDataLoadStatusRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['items'])) {
        this.items = [] as any;
        for (let item of _data['items']) this.items!.push(DataLoadStatusRequestItem.fromJS(item));
      }
    }
  }

  static fromJS(data: any): DataLoadStatusRequest {
    data = typeof data === 'object' ? data : {};
    let result = new DataLoadStatusRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.items)) {
      data['items'] = [];
      for (let item of this.items) data['items'].push(item ? item.toJSON() : (undefined as any));
    }
    return data;
  }
}

export interface IDataLoadStatusRequest {
  items?: DataLoadStatusRequestItem[] | undefined;
}

export class DataLoadStatusRequestItem implements IDataLoadStatusRequestItem {
  clientId?: number;
  appointmentId?: number;

  constructor(data?: IDataLoadStatusRequestItem) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.clientId = _data['clientId'];
      this.appointmentId = _data['appointmentId'];
    }
  }

  static fromJS(data: any): DataLoadStatusRequestItem {
    data = typeof data === 'object' ? data : {};
    let result = new DataLoadStatusRequestItem();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['clientId'] = this.clientId;
    data['appointmentId'] = this.appointmentId;
    return data;
  }
}

export interface IDataLoadStatusRequestItem {
  clientId?: number;
  appointmentId?: number;
}

export class DataLoadStatusResponse implements IDataLoadStatusResponse {
  clientId?: number;
  appointmentId?: number;
  status?: string | undefined;
  requestedAt?: string;
  statusAt?: string;
  failureReason?: string | undefined;
  loadType?: MemberDataLoadType;

  constructor(data?: IDataLoadStatusResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.clientId = _data['clientId'];
      this.appointmentId = _data['appointmentId'];
      this.status = _data['status'];
      this.requestedAt = _data['requestedAt'];
      this.statusAt = _data['statusAt'];
      this.failureReason = _data['failureReason'];
      this.loadType = _data['loadType'];
    }
  }

  static fromJS(data: any): DataLoadStatusResponse {
    data = typeof data === 'object' ? data : {};
    let result = new DataLoadStatusResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['clientId'] = this.clientId;
    data['appointmentId'] = this.appointmentId;
    data['status'] = this.status;
    data['requestedAt'] = this.requestedAt;
    data['statusAt'] = this.statusAt;
    data['failureReason'] = this.failureReason;
    data['loadType'] = this.loadType;
    return data;
  }
}

export interface IDataLoadStatusResponse {
  clientId?: number;
  appointmentId?: number;
  status?: string | undefined;
  requestedAt?: string;
  statusAt?: string;
  failureReason?: string | undefined;
  loadType?: MemberDataLoadType;
}

export class DataLoadTriggerResponse implements IDataLoadTriggerResponse {
  status?: string | undefined;
  runId?: string | undefined;

  constructor(data?: IDataLoadTriggerResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.status = _data['status'];
      this.runId = _data['runId'];
    }
  }

  static fromJS(data: any): DataLoadTriggerResponse {
    data = typeof data === 'object' ? data : {};
    let result = new DataLoadTriggerResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['status'] = this.status;
    data['runId'] = this.runId;
    return data;
  }
}

export interface IDataLoadTriggerResponse {
  status?: string | undefined;
  runId?: string | undefined;
}

export class DeaTakebackSiteResponse implements IDeaTakebackSiteResponse {
  deaTakebackSiteId?: number;
  businessName?: string | undefined;
  address1?: string | undefined;
  address2?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  zipCode?: string | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  additionalInfo?: string | undefined;
  distanceInMiles?: number | undefined;

  constructor(data?: IDeaTakebackSiteResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.deaTakebackSiteId = _data['deaTakebackSiteId'];
      this.businessName = _data['businessName'];
      this.address1 = _data['address1'];
      this.address2 = _data['address2'];
      this.city = _data['city'];
      this.state = _data['state'];
      this.zipCode = _data['zipCode'];
      this.latitude = _data['latitude'];
      this.longitude = _data['longitude'];
      this.additionalInfo = _data['additionalInfo'];
      this.distanceInMiles = _data['distanceInMiles'];
    }
  }

  static fromJS(data: any): DeaTakebackSiteResponse {
    data = typeof data === 'object' ? data : {};
    let result = new DeaTakebackSiteResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['deaTakebackSiteId'] = this.deaTakebackSiteId;
    data['businessName'] = this.businessName;
    data['address1'] = this.address1;
    data['address2'] = this.address2;
    data['city'] = this.city;
    data['state'] = this.state;
    data['zipCode'] = this.zipCode;
    data['latitude'] = this.latitude;
    data['longitude'] = this.longitude;
    data['additionalInfo'] = this.additionalInfo;
    data['distanceInMiles'] = this.distanceInMiles;
    return data;
  }
}

export interface IDeaTakebackSiteResponse {
  deaTakebackSiteId?: number;
  businessName?: string | undefined;
  address1?: string | undefined;
  address2?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  zipCode?: string | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  additionalInfo?: string | undefined;
  distanceInMiles?: number | undefined;
}

export class DiagnosisCediResponse implements IDiagnosisCediResponse {
  diagnosisName?: string | undefined;
  hccCode?: string | undefined;
  hccCodeId?: number | undefined;
  serviceDate?: string | undefined;

  constructor(data?: IDiagnosisCediResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.diagnosisName = _data['diagnosisName'];
      this.hccCode = _data['hccCode'];
      this.hccCodeId = _data['hccCodeId'];
      this.serviceDate = _data['serviceDate'];
    }
  }

  static fromJS(data: any): DiagnosisCediResponse {
    data = typeof data === 'object' ? data : {};
    let result = new DiagnosisCediResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['diagnosisName'] = this.diagnosisName;
    data['hccCode'] = this.hccCode;
    data['hccCodeId'] = this.hccCodeId;
    data['serviceDate'] = this.serviceDate;
    return data;
  }
}

export interface IDiagnosisCediResponse {
  diagnosisName?: string | undefined;
  hccCode?: string | undefined;
  hccCodeId?: number | undefined;
  serviceDate?: string | undefined;
}

export class DiagnosisClaimResponse implements IDiagnosisClaimResponse {
  diagnosisNames?: string | undefined;
  icdCodes?: string | undefined;
  serviceDate?: string | undefined;

  constructor(data?: IDiagnosisClaimResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.diagnosisNames = _data['diagnosisNames'];
      this.icdCodes = _data['icdCodes'];
      this.serviceDate = _data['serviceDate'];
    }
  }

  static fromJS(data: any): DiagnosisClaimResponse {
    data = typeof data === 'object' ? data : {};
    let result = new DiagnosisClaimResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['diagnosisNames'] = this.diagnosisNames;
    data['icdCodes'] = this.icdCodes;
    data['serviceDate'] = this.serviceDate;
    return data;
  }
}

export interface IDiagnosisClaimResponse {
  diagnosisNames?: string | undefined;
  icdCodes?: string | undefined;
  serviceDate?: string | undefined;
}

export class DiagnosisUCCResponse implements IDiagnosisUCCResponse {
  diagnosisNames?: string | undefined;
  icdCodes?: string | undefined;
  serviceDate?: string | undefined;

  constructor(data?: IDiagnosisUCCResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.diagnosisNames = _data['diagnosisNames'];
      this.icdCodes = _data['icdCodes'];
      this.serviceDate = _data['serviceDate'];
    }
  }

  static fromJS(data: any): DiagnosisUCCResponse {
    data = typeof data === 'object' ? data : {};
    let result = new DiagnosisUCCResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['diagnosisNames'] = this.diagnosisNames;
    data['icdCodes'] = this.icdCodes;
    data['serviceDate'] = this.serviceDate;
    return data;
  }
}

export interface IDiagnosisUCCResponse {
  diagnosisNames?: string | undefined;
  icdCodes?: string | undefined;
  serviceDate?: string | undefined;
}

export class GlobalVariableResponse implements IGlobalVariableResponse {
  globalVariableId?: number;
  key?: string | undefined;
  value?: string | undefined;
  description?: string | undefined;

  constructor(data?: IGlobalVariableResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.globalVariableId = _data['globalVariableId'];
      this.key = _data['key'];
      this.value = _data['value'];
      this.description = _data['description'];
    }
  }

  static fromJS(data: any): GlobalVariableResponse {
    data = typeof data === 'object' ? data : {};
    let result = new GlobalVariableResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['globalVariableId'] = this.globalVariableId;
    data['key'] = this.key;
    data['value'] = this.value;
    data['description'] = this.description;
    return data;
  }
}

export interface IGlobalVariableResponse {
  globalVariableId?: number;
  key?: string | undefined;
  value?: string | undefined;
  description?: string | undefined;
}

export class HealthCheckResponse implements IHealthCheckResponse {
  status?: string | undefined;
  timestamp?: string;
  version?: string | undefined;
  environment?: string | undefined;
  details?: { [key: string]: string } | undefined;

  constructor(data?: IHealthCheckResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.status = _data['status'];
      this.timestamp = _data['timestamp'];
      this.version = _data['version'];
      this.environment = _data['environment'];
      if (_data['details']) {
        this.details = {} as any;
        for (let key in _data['details']) {
          if (_data['details'].hasOwnProperty(key)) (this.details as any)![key] = _data['details'][key];
        }
      }
    }
  }

  static fromJS(data: any): HealthCheckResponse {
    data = typeof data === 'object' ? data : {};
    let result = new HealthCheckResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['status'] = this.status;
    data['timestamp'] = this.timestamp;
    data['version'] = this.version;
    data['environment'] = this.environment;
    if (this.details) {
      data['details'] = {};
      for (let key in this.details) {
        if (this.details.hasOwnProperty(key)) (data['details'] as any)[key] = (this.details as any)[key];
      }
    }
    return data;
  }
}

export interface IHealthCheckResponse {
  status?: string | undefined;
  timestamp?: string;
  version?: string | undefined;
  environment?: string | undefined;
  details?: { [key: string]: string } | undefined;
}

export class MedicationIcdOptionResponse implements IMedicationIcdOptionResponse {
  code?: string | undefined;
  icdDescription?: string | undefined;
  icdVersion?: number | undefined;

  constructor(data?: IMedicationIcdOptionResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.code = _data['code'];
      this.icdDescription = _data['icdDescription'];
      this.icdVersion = _data['icdVersion'];
    }
  }

  static fromJS(data: any): MedicationIcdOptionResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MedicationIcdOptionResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['code'] = this.code;
    data['icdDescription'] = this.icdDescription;
    data['icdVersion'] = this.icdVersion;
    return data;
  }
}

export interface IMedicationIcdOptionResponse {
  code?: string | undefined;
  icdDescription?: string | undefined;
  icdVersion?: number | undefined;
}

export class MedicationResponse implements IMedicationResponse {
  medicationName?: string | undefined;
  availableIcdCodes?: MedicationIcdOptionResponse[] | undefined;
  availableConditionsManaged?: string[] | undefined;

  constructor(data?: IMedicationResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.medicationName = _data['medicationName'];
      if (Array.isArray(_data['availableIcdCodes'])) {
        this.availableIcdCodes = [] as any;
        for (let item of _data['availableIcdCodes'])
          this.availableIcdCodes!.push(MedicationIcdOptionResponse.fromJS(item));
      }
      if (Array.isArray(_data['availableConditionsManaged'])) {
        this.availableConditionsManaged = [] as any;
        for (let item of _data['availableConditionsManaged']) this.availableConditionsManaged!.push(item);
      }
    }
  }

  static fromJS(data: any): MedicationResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MedicationResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['medicationName'] = this.medicationName;
    if (Array.isArray(this.availableIcdCodes)) {
      data['availableIcdCodes'] = [];
      for (let item of this.availableIcdCodes)
        data['availableIcdCodes'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.availableConditionsManaged)) {
      data['availableConditionsManaged'] = [];
      for (let item of this.availableConditionsManaged) data['availableConditionsManaged'].push(item);
    }
    return data;
  }
}

export interface IMedicationResponse {
  medicationName?: string | undefined;
  availableIcdCodes?: MedicationIcdOptionResponse[] | undefined;
  availableConditionsManaged?: string[] | undefined;
}

export class MemberAppointmentResponse implements IMemberAppointmentResponse {
  memberAppointmentId?: number;
  appointmentConfirmationId?: string | undefined;
  vendorAppointmentId?: number | undefined;
  appointmentDateTime?: string;
  appointmentDateTimeZone?: string | undefined;
  visitUrl?: string | undefined;
  meetingUrl?: string | undefined;
  appointmentAddress1?: string | undefined;
  appointmentAddress2?: string | undefined;
  appointmentCity?: string | undefined;
  appointmentState?: string | undefined;
  appointmentZip?: string | undefined;
  appointmentStatusId?: number;
  interventionTypeId?: number | undefined;
  scheduledNPI?: string | undefined;
  nursePractitionerId?: number | undefined;
  clientId?: number;
  vendorId?: number;
  memberId?: number;
  memPCPClientPracID?: string | undefined;
  dataLoadStatus?: string | undefined;
  dataLoadError?: string | undefined;
  incentiveAmount?: number | undefined;
  incentiveType?: number | undefined;
  comments?: string | undefined;
  assessmentId?: number | undefined;
  assessmentStatusId?: number | undefined;
  publishedQuestionnaireId?: string | undefined;
  questionnaireResponseId?: string | undefined;
  assessmentStartTime?: string | undefined;
  assessmentSubmittedTime?: string | undefined;
  encounterDate?: string | undefined;
  awv?: boolean;

  constructor(data?: IMemberAppointmentResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.memberAppointmentId = _data['memberAppointmentId'];
      this.appointmentConfirmationId = _data['appointmentConfirmationId'];
      this.vendorAppointmentId = _data['vendorAppointmentId'];
      this.appointmentDateTime = _data['appointmentDateTime'];
      this.appointmentDateTimeZone = _data['appointmentDateTimeZone'];
      this.visitUrl = _data['visitUrl'];
      this.meetingUrl = _data['meetingUrl'];
      this.appointmentAddress1 = _data['appointmentAddress1'];
      this.appointmentAddress2 = _data['appointmentAddress2'];
      this.appointmentCity = _data['appointmentCity'];
      this.appointmentState = _data['appointmentState'];
      this.appointmentZip = _data['appointmentZip'];
      this.appointmentStatusId = _data['appointmentStatusId'];
      this.interventionTypeId = _data['interventionTypeId'];
      this.scheduledNPI = _data['scheduledNPI'];
      this.nursePractitionerId = _data['nursePractitionerId'];
      this.clientId = _data['clientId'];
      this.vendorId = _data['vendorId'];
      this.memberId = _data['memberId'];
      this.memPCPClientPracID = _data['memPCPClientPracID'];
      this.dataLoadStatus = _data['dataLoadStatus'];
      this.dataLoadError = _data['dataLoadError'];
      this.incentiveAmount = _data['incentiveAmount'];
      this.incentiveType = _data['incentiveType'];
      this.comments = _data['comments'];
      this.assessmentId = _data['assessmentId'];
      this.assessmentStatusId = _data['assessmentStatusId'];
      this.publishedQuestionnaireId = _data['publishedQuestionnaireId'];
      this.questionnaireResponseId = _data['questionnaireResponseId'];
      this.assessmentStartTime = _data['assessmentStartTime'];
      this.assessmentSubmittedTime = _data['assessmentSubmittedTime'];
      this.encounterDate = _data['encounterDate'];
      this.awv = _data['awv'];
    }
  }

  static fromJS(data: any): MemberAppointmentResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberAppointmentResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['memberAppointmentId'] = this.memberAppointmentId;
    data['appointmentConfirmationId'] = this.appointmentConfirmationId;
    data['vendorAppointmentId'] = this.vendorAppointmentId;
    data['appointmentDateTime'] = this.appointmentDateTime;
    data['appointmentDateTimeZone'] = this.appointmentDateTimeZone;
    data['visitUrl'] = this.visitUrl;
    data['meetingUrl'] = this.meetingUrl;
    data['appointmentAddress1'] = this.appointmentAddress1;
    data['appointmentAddress2'] = this.appointmentAddress2;
    data['appointmentCity'] = this.appointmentCity;
    data['appointmentState'] = this.appointmentState;
    data['appointmentZip'] = this.appointmentZip;
    data['appointmentStatusId'] = this.appointmentStatusId;
    data['interventionTypeId'] = this.interventionTypeId;
    data['scheduledNPI'] = this.scheduledNPI;
    data['nursePractitionerId'] = this.nursePractitionerId;
    data['clientId'] = this.clientId;
    data['vendorId'] = this.vendorId;
    data['memberId'] = this.memberId;
    data['memPCPClientPracID'] = this.memPCPClientPracID;
    data['dataLoadStatus'] = this.dataLoadStatus;
    data['dataLoadError'] = this.dataLoadError;
    data['incentiveAmount'] = this.incentiveAmount;
    data['incentiveType'] = this.incentiveType;
    data['comments'] = this.comments;
    data['assessmentId'] = this.assessmentId;
    data['assessmentStatusId'] = this.assessmentStatusId;
    data['publishedQuestionnaireId'] = this.publishedQuestionnaireId;
    data['questionnaireResponseId'] = this.questionnaireResponseId;
    data['assessmentStartTime'] = this.assessmentStartTime;
    data['assessmentSubmittedTime'] = this.assessmentSubmittedTime;
    data['encounterDate'] = this.encounterDate;
    data['awv'] = this.awv;
    return data;
  }
}

export interface IMemberAppointmentResponse {
  memberAppointmentId?: number;
  appointmentConfirmationId?: string | undefined;
  vendorAppointmentId?: number | undefined;
  appointmentDateTime?: string;
  appointmentDateTimeZone?: string | undefined;
  visitUrl?: string | undefined;
  meetingUrl?: string | undefined;
  appointmentAddress1?: string | undefined;
  appointmentAddress2?: string | undefined;
  appointmentCity?: string | undefined;
  appointmentState?: string | undefined;
  appointmentZip?: string | undefined;
  appointmentStatusId?: number;
  interventionTypeId?: number | undefined;
  scheduledNPI?: string | undefined;
  nursePractitionerId?: number | undefined;
  clientId?: number;
  vendorId?: number;
  memberId?: number;
  memPCPClientPracID?: string | undefined;
  dataLoadStatus?: string | undefined;
  dataLoadError?: string | undefined;
  incentiveAmount?: number | undefined;
  incentiveType?: number | undefined;
  comments?: string | undefined;
  assessmentId?: number | undefined;
  assessmentStatusId?: number | undefined;
  publishedQuestionnaireId?: string | undefined;
  questionnaireResponseId?: string | undefined;
  assessmentStartTime?: string | undefined;
  assessmentSubmittedTime?: string | undefined;
  encounterDate?: string | undefined;
  awv?: boolean;
}

export class MemberCediResponse implements IMemberCediResponse {
  diagnoses?: DiagnosisCediResponse[] | undefined;

  constructor(data?: IMemberCediResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['diagnoses'])) {
        this.diagnoses = [] as any;
        for (let item of _data['diagnoses']) this.diagnoses!.push(DiagnosisCediResponse.fromJS(item));
      }
    }
  }

  static fromJS(data: any): MemberCediResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberCediResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.diagnoses)) {
      data['diagnoses'] = [];
      for (let item of this.diagnoses) data['diagnoses'].push(item ? item.toJSON() : (undefined as any));
    }
    return data;
  }
}

export interface IMemberCediResponse {
  diagnoses?: DiagnosisCediResponse[] | undefined;
}

export class MemberClaimsResponse implements IMemberClaimsResponse {
  diagnoses?: DiagnosisClaimResponse[] | undefined;
  otherClaims?: OtherClaimResponse[] | undefined;

  constructor(data?: IMemberClaimsResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['diagnoses'])) {
        this.diagnoses = [] as any;
        for (let item of _data['diagnoses']) this.diagnoses!.push(DiagnosisClaimResponse.fromJS(item));
      }
      if (Array.isArray(_data['otherClaims'])) {
        this.otherClaims = [] as any;
        for (let item of _data['otherClaims']) this.otherClaims!.push(OtherClaimResponse.fromJS(item));
      }
    }
  }

  static fromJS(data: any): MemberClaimsResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberClaimsResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.diagnoses)) {
      data['diagnoses'] = [];
      for (let item of this.diagnoses) data['diagnoses'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.otherClaims)) {
      data['otherClaims'] = [];
      for (let item of this.otherClaims) data['otherClaims'].push(item ? item.toJSON() : (undefined as any));
    }
    return data;
  }
}

export interface IMemberClaimsResponse {
  diagnoses?: DiagnosisClaimResponse[] | undefined;
  otherClaims?: OtherClaimResponse[] | undefined;
}

export enum MemberDataLoadType {
  _0 = 0,
  _1 = 1,
}

export class MemberGapTypeResponse implements IMemberGapTypeResponse {
  gapTypeId?: number;
  measureCode?: string | undefined;
  gapName?: string | undefined;
  gapLabel?: string | undefined;

  constructor(data?: IMemberGapTypeResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.gapTypeId = _data['gapTypeId'];
      this.measureCode = _data['measureCode'];
      this.gapName = _data['gapName'];
      this.gapLabel = _data['gapLabel'];
    }
  }

  static fromJS(data: any): MemberGapTypeResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberGapTypeResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['gapTypeId'] = this.gapTypeId;
    data['measureCode'] = this.measureCode;
    data['gapName'] = this.gapName;
    data['gapLabel'] = this.gapLabel;
    return data;
  }
}

export interface IMemberGapTypeResponse {
  gapTypeId?: number;
  measureCode?: string | undefined;
  gapName?: string | undefined;
  gapLabel?: string | undefined;
}

export class MemberInfoResponse implements IMemberInfoResponse {
  member?: MemberResponse;
  primaryCarePhysician?: MemberPCPResponse;
  appointment?: MemberAppointmentResponse;
  nearestTakebackSites?: DeaTakebackSiteResponse[] | undefined;
  mailedGaps?: MemberGapTypeResponse[] | undefined;
  memberGaps?: MemberGapTypeResponse[] | undefined;

  constructor(data?: IMemberInfoResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.member = _data['member'] ? MemberResponse.fromJS(_data['member']) : (undefined as any);
      this.primaryCarePhysician = _data['primaryCarePhysician']
        ? MemberPCPResponse.fromJS(_data['primaryCarePhysician'])
        : (undefined as any);
      this.appointment = _data['appointment']
        ? MemberAppointmentResponse.fromJS(_data['appointment'])
        : (undefined as any);
      if (Array.isArray(_data['nearestTakebackSites'])) {
        this.nearestTakebackSites = [] as any;
        for (let item of _data['nearestTakebackSites'])
          this.nearestTakebackSites!.push(DeaTakebackSiteResponse.fromJS(item));
      }
      if (Array.isArray(_data['mailedGaps'])) {
        this.mailedGaps = [] as any;
        for (let item of _data['mailedGaps']) this.mailedGaps!.push(MemberGapTypeResponse.fromJS(item));
      }
      if (Array.isArray(_data['memberGaps'])) {
        this.memberGaps = [] as any;
        for (let item of _data['memberGaps']) this.memberGaps!.push(MemberGapTypeResponse.fromJS(item));
      }
    }
  }

  static fromJS(data: any): MemberInfoResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberInfoResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['member'] = this.member ? this.member.toJSON() : (undefined as any);
    data['primaryCarePhysician'] = this.primaryCarePhysician ? this.primaryCarePhysician.toJSON() : (undefined as any);
    data['appointment'] = this.appointment ? this.appointment.toJSON() : (undefined as any);
    if (Array.isArray(this.nearestTakebackSites)) {
      data['nearestTakebackSites'] = [];
      for (let item of this.nearestTakebackSites)
        data['nearestTakebackSites'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.mailedGaps)) {
      data['mailedGaps'] = [];
      for (let item of this.mailedGaps) data['mailedGaps'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.memberGaps)) {
      data['memberGaps'] = [];
      for (let item of this.memberGaps) data['memberGaps'].push(item ? item.toJSON() : (undefined as any));
    }
    return data;
  }
}

export interface IMemberInfoResponse {
  member?: MemberResponse;
  primaryCarePhysician?: MemberPCPResponse;
  appointment?: MemberAppointmentResponse;
  nearestTakebackSites?: DeaTakebackSiteResponse[] | undefined;
  mailedGaps?: MemberGapTypeResponse[] | undefined;
  memberGaps?: MemberGapTypeResponse[] | undefined;
}

export class MemberMedicationsResponse implements IMemberMedicationsResponse {
  medications?: MedicationResponse[] | undefined;

  constructor(data?: IMemberMedicationsResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['medications'])) {
        this.medications = [] as any;
        for (let item of _data['medications']) this.medications!.push(MedicationResponse.fromJS(item));
      }
    }
  }

  static fromJS(data: any): MemberMedicationsResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberMedicationsResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.medications)) {
      data['medications'] = [];
      for (let item of this.medications) data['medications'].push(item ? item.toJSON() : (undefined as any));
    }
    return data;
  }
}

export interface IMemberMedicationsResponse {
  medications?: MedicationResponse[] | undefined;
}

export class MemberPCPResponse implements IMemberPCPResponse {
  name?: string | undefined;
  npiNumber?: string | undefined;
  phoneNumber?: string | undefined;
  faxNumber?: string | undefined;
  address?: string | undefined;
  state?: string | undefined;
  effectiveDate?: string | undefined;

  constructor(data?: IMemberPCPResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.name = _data['name'];
      this.npiNumber = _data['npiNumber'];
      this.phoneNumber = _data['phoneNumber'];
      this.faxNumber = _data['faxNumber'];
      this.address = _data['address'];
      this.state = _data['state'];
      this.effectiveDate = _data['effectiveDate'];
    }
  }

  static fromJS(data: any): MemberPCPResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberPCPResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['name'] = this.name;
    data['npiNumber'] = this.npiNumber;
    data['phoneNumber'] = this.phoneNumber;
    data['faxNumber'] = this.faxNumber;
    data['address'] = this.address;
    data['state'] = this.state;
    data['effectiveDate'] = this.effectiveDate;
    return data;
  }
}

export interface IMemberPCPResponse {
  name?: string | undefined;
  npiNumber?: string | undefined;
  phoneNumber?: string | undefined;
  faxNumber?: string | undefined;
  address?: string | undefined;
  state?: string | undefined;
  effectiveDate?: string | undefined;
}

export class MemberResponse implements IMemberResponse {
  memberId?: number;
  name?: string | undefined;
  gender?: string | undefined;
  dateOfBirth?: string | undefined;
  email?: string | undefined;
  mailingAddress?: string | undefined;
  state?: string | undefined;
  primaryPhoneNumber?: string | undefined;
  clientMemberId?: string | undefined;
  brandName?: string | undefined;
  planCode?: string | undefined;
  lineOfBusiness?: string | undefined;
  hra?: boolean;

  constructor(data?: IMemberResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.memberId = _data['memberId'];
      this.name = _data['name'];
      this.gender = _data['gender'];
      this.dateOfBirth = _data['dateOfBirth'];
      this.email = _data['email'];
      this.mailingAddress = _data['mailingAddress'];
      this.state = _data['state'];
      this.primaryPhoneNumber = _data['primaryPhoneNumber'];
      this.clientMemberId = _data['clientMemberId'];
      this.brandName = _data['brandName'];
      this.planCode = _data['planCode'];
      this.lineOfBusiness = _data['lineOfBusiness'];
      this.hra = _data['hra'];
    }
  }

  static fromJS(data: any): MemberResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['memberId'] = this.memberId;
    data['name'] = this.name;
    data['gender'] = this.gender;
    data['dateOfBirth'] = this.dateOfBirth;
    data['email'] = this.email;
    data['mailingAddress'] = this.mailingAddress;
    data['state'] = this.state;
    data['primaryPhoneNumber'] = this.primaryPhoneNumber;
    data['clientMemberId'] = this.clientMemberId;
    data['brandName'] = this.brandName;
    data['planCode'] = this.planCode;
    data['lineOfBusiness'] = this.lineOfBusiness;
    data['hra'] = this.hra;
    return data;
  }
}

export interface IMemberResponse {
  memberId?: number;
  name?: string | undefined;
  gender?: string | undefined;
  dateOfBirth?: string | undefined;
  email?: string | undefined;
  mailingAddress?: string | undefined;
  state?: string | undefined;
  primaryPhoneNumber?: string | undefined;
  clientMemberId?: string | undefined;
  brandName?: string | undefined;
  planCode?: string | undefined;
  lineOfBusiness?: string | undefined;
  hra?: boolean;
}

export class MemberUCCResponse implements IMemberUCCResponse {
  diagnoses?: DiagnosisUCCResponse[] | undefined;

  constructor(data?: IMemberUCCResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['diagnoses'])) {
        this.diagnoses = [] as any;
        for (let item of _data['diagnoses']) this.diagnoses!.push(DiagnosisUCCResponse.fromJS(item));
      }
    }
  }

  static fromJS(data: any): MemberUCCResponse {
    data = typeof data === 'object' ? data : {};
    let result = new MemberUCCResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.diagnoses)) {
      data['diagnoses'] = [];
      for (let item of this.diagnoses) data['diagnoses'].push(item ? item.toJSON() : (undefined as any));
    }
    return data;
  }
}

export interface IMemberUCCResponse {
  diagnoses?: DiagnosisUCCResponse[] | undefined;
}

export class NursePractitionerLicenseResponse implements INursePractitionerLicenseResponse {
  licenseId?: number;
  licenseName?: string | undefined;
  issuedBy?: string | undefined;
  expirationDate?: string | undefined;
  isTelehealth?: boolean;

  constructor(data?: INursePractitionerLicenseResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.licenseId = _data['licenseId'];
      this.licenseName = _data['licenseName'];
      this.issuedBy = _data['issuedBy'];
      this.expirationDate = _data['expirationDate'];
      this.isTelehealth = _data['isTelehealth'];
    }
  }

  static fromJS(data: any): NursePractitionerLicenseResponse {
    data = typeof data === 'object' ? data : {};
    let result = new NursePractitionerLicenseResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['licenseId'] = this.licenseId;
    data['licenseName'] = this.licenseName;
    data['issuedBy'] = this.issuedBy;
    data['expirationDate'] = this.expirationDate;
    data['isTelehealth'] = this.isTelehealth;
    return data;
  }
}

export interface INursePractitionerLicenseResponse {
  licenseId?: number;
  licenseName?: string | undefined;
  issuedBy?: string | undefined;
  expirationDate?: string | undefined;
  isTelehealth?: boolean;
}

export class NursePractitionerMedicaidLicenseResponse implements INursePractitionerMedicaidLicenseResponse {
  nursePractitionerMedicaidLicenseId?: number;
  medicaidNumber?: string | undefined;
  state?: string | undefined;
  expirationDate?: string | undefined;
  clients?: string | undefined;

  constructor(data?: INursePractitionerMedicaidLicenseResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.nursePractitionerMedicaidLicenseId = _data['nursePractitionerMedicaidLicenseId'];
      this.medicaidNumber = _data['medicaidNumber'];
      this.state = _data['state'];
      this.expirationDate = _data['expirationDate'];
      this.clients = _data['clients'];
    }
  }

  static fromJS(data: any): NursePractitionerMedicaidLicenseResponse {
    data = typeof data === 'object' ? data : {};
    let result = new NursePractitionerMedicaidLicenseResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['nursePractitionerMedicaidLicenseId'] = this.nursePractitionerMedicaidLicenseId;
    data['medicaidNumber'] = this.medicaidNumber;
    data['state'] = this.state;
    data['expirationDate'] = this.expirationDate;
    data['clients'] = this.clients;
    return data;
  }
}

export interface INursePractitionerMedicaidLicenseResponse {
  nursePractitionerMedicaidLicenseId?: number;
  medicaidNumber?: string | undefined;
  state?: string | undefined;
  expirationDate?: string | undefined;
  clients?: string | undefined;
}

export class NursePractitionerOptionResponse implements INursePractitionerOptionResponse {
  nursePractitionerId?: number;
  firstName?: string | undefined;
  lastName?: string | undefined;
  emailAddress?: string | undefined;

  constructor(data?: INursePractitionerOptionResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.nursePractitionerId = _data['nursePractitionerId'];
      this.firstName = _data['firstName'];
      this.lastName = _data['lastName'];
      this.emailAddress = _data['emailAddress'];
    }
  }

  static fromJS(data: any): NursePractitionerOptionResponse {
    data = typeof data === 'object' ? data : {};
    let result = new NursePractitionerOptionResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['nursePractitionerId'] = this.nursePractitionerId;
    data['firstName'] = this.firstName;
    data['lastName'] = this.lastName;
    data['emailAddress'] = this.emailAddress;
    return data;
  }
}

export interface INursePractitionerOptionResponse {
  nursePractitionerId?: number;
  firstName?: string | undefined;
  lastName?: string | undefined;
  emailAddress?: string | undefined;
}

export class NursePractitionerProfileResponse implements INursePractitionerProfileResponse {
  firstName?: string | undefined;
  lastName?: string | undefined;
  npi?: string | undefined;
  phone?: string | undefined;
  specialty?: string | undefined;
  timeZone?: string | undefined;
  clinicalManager?: string | undefined;
  yearsInPractice?: string | undefined;
  totalPatients?: number;
  stats?: NursePractitionerStatsResponse[] | undefined;
  licenses?: NursePractitionerLicenseResponse[] | undefined;
  medicaidLicenses?: NursePractitionerMedicaidLicenseResponse[] | undefined;
  credentials?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  country?: string | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;

  constructor(data?: INursePractitionerProfileResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.firstName = _data['firstName'];
      this.lastName = _data['lastName'];
      this.npi = _data['npi'];
      this.phone = _data['phone'];
      this.specialty = _data['specialty'];
      this.timeZone = _data['timeZone'];
      this.clinicalManager = _data['clinicalManager'];
      this.yearsInPractice = _data['yearsInPractice'];
      this.totalPatients = _data['totalPatients'];
      if (Array.isArray(_data['stats'])) {
        this.stats = [] as any;
        for (let item of _data['stats']) this.stats!.push(NursePractitionerStatsResponse.fromJS(item));
      }
      if (Array.isArray(_data['licenses'])) {
        this.licenses = [] as any;
        for (let item of _data['licenses']) this.licenses!.push(NursePractitionerLicenseResponse.fromJS(item));
      }
      if (Array.isArray(_data['medicaidLicenses'])) {
        this.medicaidLicenses = [] as any;
        for (let item of _data['medicaidLicenses'])
          this.medicaidLicenses!.push(NursePractitionerMedicaidLicenseResponse.fromJS(item));
      }
      this.credentials = _data['credentials'];
      this.city = _data['city'];
      this.state = _data['state'];
      this.country = _data['country'];
      this.latitude = _data['latitude'];
      this.longitude = _data['longitude'];
    }
  }

  static fromJS(data: any): NursePractitionerProfileResponse {
    data = typeof data === 'object' ? data : {};
    let result = new NursePractitionerProfileResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['firstName'] = this.firstName;
    data['lastName'] = this.lastName;
    data['npi'] = this.npi;
    data['phone'] = this.phone;
    data['specialty'] = this.specialty;
    data['timeZone'] = this.timeZone;
    data['clinicalManager'] = this.clinicalManager;
    data['yearsInPractice'] = this.yearsInPractice;
    data['totalPatients'] = this.totalPatients;
    if (Array.isArray(this.stats)) {
      data['stats'] = [];
      for (let item of this.stats) data['stats'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.licenses)) {
      data['licenses'] = [];
      for (let item of this.licenses) data['licenses'].push(item ? item.toJSON() : (undefined as any));
    }
    if (Array.isArray(this.medicaidLicenses)) {
      data['medicaidLicenses'] = [];
      for (let item of this.medicaidLicenses) data['medicaidLicenses'].push(item ? item.toJSON() : (undefined as any));
    }
    data['credentials'] = this.credentials;
    data['city'] = this.city;
    data['state'] = this.state;
    data['country'] = this.country;
    data['latitude'] = this.latitude;
    data['longitude'] = this.longitude;
    return data;
  }
}

export interface INursePractitionerProfileResponse {
  firstName?: string | undefined;
  lastName?: string | undefined;
  npi?: string | undefined;
  phone?: string | undefined;
  specialty?: string | undefined;
  timeZone?: string | undefined;
  clinicalManager?: string | undefined;
  yearsInPractice?: string | undefined;
  totalPatients?: number;
  stats?: NursePractitionerStatsResponse[] | undefined;
  licenses?: NursePractitionerLicenseResponse[] | undefined;
  medicaidLicenses?: NursePractitionerMedicaidLicenseResponse[] | undefined;
  credentials?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  country?: string | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
}

export class NursePractitionerStatsResponse implements INursePractitionerStatsResponse {
  nursePractitionerId?: number;
  interventionTypeId?: number | undefined;
  interventionCount?: number;
  totalMembersCount?: number;
  computedAtUtc?: string;

  constructor(data?: INursePractitionerStatsResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.nursePractitionerId = _data['nursePractitionerId'];
      this.interventionTypeId = _data['interventionTypeId'];
      this.interventionCount = _data['interventionCount'];
      this.totalMembersCount = _data['totalMembersCount'];
      this.computedAtUtc = _data['computedAtUtc'];
    }
  }

  static fromJS(data: any): NursePractitionerStatsResponse {
    data = typeof data === 'object' ? data : {};
    let result = new NursePractitionerStatsResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['nursePractitionerId'] = this.nursePractitionerId;
    data['interventionTypeId'] = this.interventionTypeId;
    data['interventionCount'] = this.interventionCount;
    data['totalMembersCount'] = this.totalMembersCount;
    data['computedAtUtc'] = this.computedAtUtc;
    return data;
  }
}

export interface INursePractitionerStatsResponse {
  nursePractitionerId?: number;
  interventionTypeId?: number | undefined;
  interventionCount?: number;
  totalMembersCount?: number;
  computedAtUtc?: string;
}

export class OtherClaimResponse implements IOtherClaimResponse {
  claimNames?: string | undefined;
  codes?: string | undefined;
  codeType?: string | undefined;
  dateOfClaim?: string;

  constructor(data?: IOtherClaimResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.claimNames = _data['claimNames'];
      this.codes = _data['codes'];
      this.codeType = _data['codeType'];
      this.dateOfClaim = _data['dateOfClaim'];
    }
  }

  static fromJS(data: any): OtherClaimResponse {
    data = typeof data === 'object' ? data : {};
    let result = new OtherClaimResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['claimNames'] = this.claimNames;
    data['codes'] = this.codes;
    data['codeType'] = this.codeType;
    data['dateOfClaim'] = this.dateOfClaim;
    return data;
  }
}

export interface IOtherClaimResponse {
  claimNames?: string | undefined;
  codes?: string | undefined;
  codeType?: string | undefined;
  dateOfClaim?: string;
}

export class OverdueAssessmentResponse implements IOverdueAssessmentResponse {
  appointmentId?: number;
  assessmentId?: number;
  clientId?: number;
  memberFirstName?: string | undefined;
  memberLastName?: string | undefined;
  appointmentDateTime?: string;
  assessmentStatusId?: number;
  overdueDays?: number;

  constructor(data?: IOverdueAssessmentResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.appointmentId = _data['appointmentId'];
      this.assessmentId = _data['assessmentId'];
      this.clientId = _data['clientId'];
      this.memberFirstName = _data['memberFirstName'];
      this.memberLastName = _data['memberLastName'];
      this.appointmentDateTime = _data['appointmentDateTime'];
      this.assessmentStatusId = _data['assessmentStatusId'];
      this.overdueDays = _data['overdueDays'];
    }
  }

  static fromJS(data: any): OverdueAssessmentResponse {
    data = typeof data === 'object' ? data : {};
    let result = new OverdueAssessmentResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['appointmentId'] = this.appointmentId;
    data['assessmentId'] = this.assessmentId;
    data['clientId'] = this.clientId;
    data['memberFirstName'] = this.memberFirstName;
    data['memberLastName'] = this.memberLastName;
    data['appointmentDateTime'] = this.appointmentDateTime;
    data['assessmentStatusId'] = this.assessmentStatusId;
    data['overdueDays'] = this.overdueDays;
    return data;
  }
}

export interface IOverdueAssessmentResponse {
  appointmentId?: number;
  assessmentId?: number;
  clientId?: number;
  memberFirstName?: string | undefined;
  memberLastName?: string | undefined;
  appointmentDateTime?: string;
  assessmentStatusId?: number;
  overdueDays?: number;
}

export class PractitionerAppointmentResponse implements IPractitionerAppointmentResponse {
  appointmentId?: number;
  memberId?: number;
  practitionerId?: number;
  patientName?: string | undefined;
  interventionType?: string | undefined;
  age?: number | undefined;
  gender?: string | undefined;
  clientId?: number;
  clientName?: string | undefined;
  clientMemberId?: string | undefined;
  npName?: string | undefined;
  vendorName?: string | undefined;
  npi?: string | undefined;
  appointmentStatus?: string | undefined;
  appointmentStatusGroup?: string | undefined;
  dataLoadStatus?: string | undefined;
  dataLoadRequestedAt?: string | undefined;
  dataLoadStatusAt?: string | undefined;
  pdfDenormStatus?: string | undefined;
  pdfDenormRequestedAt?: string | undefined;
  pdfDenormStatusAt?: string | undefined;
  assessmentStatus?: string | undefined;
  appointmentDateTime?: string;
  phoneNumber?: string | undefined;
  assessmentId?: number | undefined;
  assessmentStatusId?: number | undefined;
  questionnaireResponseId?: string | undefined;
  assessmentStartedDateTime?: string | undefined;
  assessmentSubmittedDateTime?: string | undefined;
  isAmended?: boolean;
  meetingUrl?: string | undefined;
  lobId?: number;
  assessmentArchivalreasonId?: number | undefined;
  assessmentArchivedBy?: string | undefined;
  assessmentArchivalOtherReason?: string | undefined;
  isSubmittedOffline?: boolean;
  canStartAssessment?: boolean;
  canStartAssessmentReason?: string | undefined;
  isAssignedToCurrentUser?: boolean;
  availablePdfs?: AssessmentPdfMetadata[] | undefined;

  constructor(data?: IPractitionerAppointmentResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.appointmentId = _data['appointmentId'];
      this.memberId = _data['memberId'];
      this.practitionerId = _data['practitionerId'];
      this.patientName = _data['patientName'];
      this.interventionType = _data['interventionType'];
      this.age = _data['age'];
      this.gender = _data['gender'];
      this.clientId = _data['clientId'];
      this.clientName = _data['clientName'];
      this.clientMemberId = _data['clientMemberId'];
      this.npName = _data['npName'];
      this.vendorName = _data['vendorName'];
      this.npi = _data['npi'];
      this.appointmentStatus = _data['appointmentStatus'];
      this.appointmentStatusGroup = _data['appointmentStatusGroup'];
      this.dataLoadStatus = _data['dataLoadStatus'];
      this.dataLoadRequestedAt = _data['dataLoadRequestedAt'];
      this.dataLoadStatusAt = _data['dataLoadStatusAt'];
      this.pdfDenormStatus = _data['pdfDenormStatus'];
      this.pdfDenormRequestedAt = _data['pdfDenormRequestedAt'];
      this.pdfDenormStatusAt = _data['pdfDenormStatusAt'];
      this.assessmentStatus = _data['assessmentStatus'];
      this.appointmentDateTime = _data['appointmentDateTime'];
      this.phoneNumber = _data['phoneNumber'];
      this.assessmentId = _data['assessmentId'];
      this.assessmentStatusId = _data['assessmentStatusId'];
      this.questionnaireResponseId = _data['questionnaireResponseId'];
      this.assessmentStartedDateTime = _data['assessmentStartedDateTime'];
      this.assessmentSubmittedDateTime = _data['assessmentSubmittedDateTime'];
      this.isAmended = _data['isAmended'];
      this.meetingUrl = _data['meetingUrl'];
      this.lobId = _data['lobId'];
      this.assessmentArchivalreasonId = _data['assessmentArchivalreasonId'];
      this.assessmentArchivedBy = _data['assessmentArchivedBy'];
      this.assessmentArchivalOtherReason = _data['assessmentArchivalOtherReason'];
      this.isSubmittedOffline = _data['isSubmittedOffline'];
      this.canStartAssessment = _data['canStartAssessment'];
      this.canStartAssessmentReason = _data['canStartAssessmentReason'];
      this.isAssignedToCurrentUser = _data['isAssignedToCurrentUser'];
      if (Array.isArray(_data['availablePdfs'])) {
        this.availablePdfs = [] as any;
        for (let item of _data['availablePdfs']) this.availablePdfs!.push(AssessmentPdfMetadata.fromJS(item));
      }
    }
  }

  static fromJS(data: any): PractitionerAppointmentResponse {
    data = typeof data === 'object' ? data : {};
    let result = new PractitionerAppointmentResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['appointmentId'] = this.appointmentId;
    data['memberId'] = this.memberId;
    data['practitionerId'] = this.practitionerId;
    data['patientName'] = this.patientName;
    data['interventionType'] = this.interventionType;
    data['age'] = this.age;
    data['gender'] = this.gender;
    data['clientId'] = this.clientId;
    data['clientName'] = this.clientName;
    data['clientMemberId'] = this.clientMemberId;
    data['npName'] = this.npName;
    data['vendorName'] = this.vendorName;
    data['npi'] = this.npi;
    data['appointmentStatus'] = this.appointmentStatus;
    data['appointmentStatusGroup'] = this.appointmentStatusGroup;
    data['dataLoadStatus'] = this.dataLoadStatus;
    data['dataLoadRequestedAt'] = this.dataLoadRequestedAt;
    data['dataLoadStatusAt'] = this.dataLoadStatusAt;
    data['pdfDenormStatus'] = this.pdfDenormStatus;
    data['pdfDenormRequestedAt'] = this.pdfDenormRequestedAt;
    data['pdfDenormStatusAt'] = this.pdfDenormStatusAt;
    data['assessmentStatus'] = this.assessmentStatus;
    data['appointmentDateTime'] = this.appointmentDateTime;
    data['phoneNumber'] = this.phoneNumber;
    data['assessmentId'] = this.assessmentId;
    data['assessmentStatusId'] = this.assessmentStatusId;
    data['questionnaireResponseId'] = this.questionnaireResponseId;
    data['assessmentStartedDateTime'] = this.assessmentStartedDateTime;
    data['assessmentSubmittedDateTime'] = this.assessmentSubmittedDateTime;
    data['isAmended'] = this.isAmended;
    data['meetingUrl'] = this.meetingUrl;
    data['lobId'] = this.lobId;
    data['assessmentArchivalreasonId'] = this.assessmentArchivalreasonId;
    data['assessmentArchivedBy'] = this.assessmentArchivedBy;
    data['assessmentArchivalOtherReason'] = this.assessmentArchivalOtherReason;
    data['isSubmittedOffline'] = this.isSubmittedOffline;
    data['canStartAssessment'] = this.canStartAssessment;
    data['canStartAssessmentReason'] = this.canStartAssessmentReason;
    data['isAssignedToCurrentUser'] = this.isAssignedToCurrentUser;
    if (Array.isArray(this.availablePdfs)) {
      data['availablePdfs'] = [];
      for (let item of this.availablePdfs) data['availablePdfs'].push(item ? item.toJSON() : (undefined as any));
    }
    return data;
  }
}

export interface IPractitionerAppointmentResponse {
  appointmentId?: number;
  memberId?: number;
  practitionerId?: number;
  patientName?: string | undefined;
  interventionType?: string | undefined;
  age?: number | undefined;
  gender?: string | undefined;
  clientId?: number;
  clientName?: string | undefined;
  clientMemberId?: string | undefined;
  npName?: string | undefined;
  vendorName?: string | undefined;
  npi?: string | undefined;
  appointmentStatus?: string | undefined;
  appointmentStatusGroup?: string | undefined;
  dataLoadStatus?: string | undefined;
  dataLoadRequestedAt?: string | undefined;
  dataLoadStatusAt?: string | undefined;
  pdfDenormStatus?: string | undefined;
  pdfDenormRequestedAt?: string | undefined;
  pdfDenormStatusAt?: string | undefined;
  assessmentStatus?: string | undefined;
  appointmentDateTime?: string;
  phoneNumber?: string | undefined;
  assessmentId?: number | undefined;
  assessmentStatusId?: number | undefined;
  questionnaireResponseId?: string | undefined;
  assessmentStartedDateTime?: string | undefined;
  assessmentSubmittedDateTime?: string | undefined;
  isAmended?: boolean;
  meetingUrl?: string | undefined;
  lobId?: number;
  assessmentArchivalreasonId?: number | undefined;
  assessmentArchivedBy?: string | undefined;
  assessmentArchivalOtherReason?: string | undefined;
  isSubmittedOffline?: boolean;
  canStartAssessment?: boolean;
  canStartAssessmentReason?: string | undefined;
  isAssignedToCurrentUser?: boolean;
  availablePdfs?: AssessmentPdfMetadata[] | undefined;
}

export class PractitionerAppointmentResponsePagedResult implements IPractitionerAppointmentResponsePagedResult {
  items?: PractitionerAppointmentResponse[] | undefined;
  readonly pageIndex?: number;
  readonly pageSize?: number;
  totalItems?: number;
  readonly totalPages?: number;
  readonly hasPreviousPage?: boolean;
  readonly hasNextPage?: boolean;

  constructor(data?: IPractitionerAppointmentResponsePagedResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['items'])) {
        this.items = [] as any;
        for (let item of _data['items']) this.items!.push(PractitionerAppointmentResponse.fromJS(item));
      }
      (this as any).pageIndex = _data['pageIndex'];
      (this as any).pageSize = _data['pageSize'];
      this.totalItems = _data['totalItems'];
      (this as any).totalPages = _data['totalPages'];
      (this as any).hasPreviousPage = _data['hasPreviousPage'];
      (this as any).hasNextPage = _data['hasNextPage'];
    }
  }

  static fromJS(data: any): PractitionerAppointmentResponsePagedResult {
    data = typeof data === 'object' ? data : {};
    let result = new PractitionerAppointmentResponsePagedResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.items)) {
      data['items'] = [];
      for (let item of this.items) data['items'].push(item ? item.toJSON() : (undefined as any));
    }
    data['pageIndex'] = this.pageIndex;
    data['pageSize'] = this.pageSize;
    data['totalItems'] = this.totalItems;
    data['totalPages'] = this.totalPages;
    data['hasPreviousPage'] = this.hasPreviousPage;
    data['hasNextPage'] = this.hasNextPage;
    return data;
  }
}

export interface IPractitionerAppointmentResponsePagedResult {
  items?: PractitionerAppointmentResponse[] | undefined;
  pageIndex?: number;
  pageSize?: number;
  totalItems?: number;
  totalPages?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
}

export class ProblemDetails implements IProblemDetails {
  type?: string | undefined;
  title?: string | undefined;
  status?: number | undefined;
  detail?: string | undefined;
  instance?: string | undefined;

  [key: string]: any;

  constructor(data?: IProblemDetails) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      for (var property in _data) {
        if (_data.hasOwnProperty(property)) this[property] = _data[property];
      }
      this.type = _data['type'];
      this.title = _data['title'];
      this.status = _data['status'];
      this.detail = _data['detail'];
      this.instance = _data['instance'];
    }
  }

  static fromJS(data: any): ProblemDetails {
    data = typeof data === 'object' ? data : {};
    let result = new ProblemDetails();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    for (var property in this) {
      if (this.hasOwnProperty(property)) data[property] = this[property];
    }
    data['type'] = this.type;
    data['title'] = this.title;
    data['status'] = this.status;
    data['detail'] = this.detail;
    data['instance'] = this.instance;
    return data;
  }
}

export interface IProblemDetails {
  type?: string | undefined;
  title?: string | undefined;
  status?: number | undefined;
  detail?: string | undefined;
  instance?: string | undefined;

  [key: string]: any;
}

export class ProviderSearchRequest implements IProviderSearchRequest {
  page?: number;
  pageSize?: number;
  readonly skip?: number;
  lastName?: string | undefined;
  firstName?: string | undefined;
  specialty?: string | undefined;
  npi?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  sortColumn?: string | undefined;
  sortDirection?: string | undefined;

  constructor(data?: IProviderSearchRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.page = _data['page'];
      this.pageSize = _data['pageSize'];
      (this as any).skip = _data['skip'];
      this.lastName = _data['lastName'];
      this.firstName = _data['firstName'];
      this.specialty = _data['specialty'];
      this.npi = _data['npi'];
      this.city = _data['city'];
      this.state = _data['state'];
      this.sortColumn = _data['sortColumn'];
      this.sortDirection = _data['sortDirection'];
    }
  }

  static fromJS(data: any): ProviderSearchRequest {
    data = typeof data === 'object' ? data : {};
    let result = new ProviderSearchRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['page'] = this.page;
    data['pageSize'] = this.pageSize;
    data['skip'] = this.skip;
    data['lastName'] = this.lastName;
    data['firstName'] = this.firstName;
    data['specialty'] = this.specialty;
    data['npi'] = this.npi;
    data['city'] = this.city;
    data['state'] = this.state;
    data['sortColumn'] = this.sortColumn;
    data['sortDirection'] = this.sortDirection;
    return data;
  }
}

export interface IProviderSearchRequest {
  page?: number;
  pageSize?: number;
  skip?: number;
  lastName?: string | undefined;
  firstName?: string | undefined;
  specialty?: string | undefined;
  npi?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  sortColumn?: string | undefined;
  sortDirection?: string | undefined;
}

export class ProviderSearchResult implements IProviderSearchResult {
  providerId?: number;
  providerSourceId?: number;
  providerSource?: string | undefined;
  clientPracId?: string | undefined;
  npi?: string | undefined;
  firstName?: string | undefined;
  middleName?: string | undefined;
  lastName?: string | undefined;
  address1?: string | undefined;
  address2?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  zip?: string | undefined;
  specialtyId?: number | undefined;
  specialty?: string | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  fullAddress?: string | undefined;
  fax?: string | undefined;

  constructor(data?: IProviderSearchResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.providerId = _data['providerId'];
      this.providerSourceId = _data['providerSourceId'];
      this.providerSource = _data['providerSource'];
      this.clientPracId = _data['clientPracId'];
      this.npi = _data['npi'];
      this.firstName = _data['firstName'];
      this.middleName = _data['middleName'];
      this.lastName = _data['lastName'];
      this.address1 = _data['address1'];
      this.address2 = _data['address2'];
      this.city = _data['city'];
      this.state = _data['state'];
      this.zip = _data['zip'];
      this.specialtyId = _data['specialtyId'];
      this.specialty = _data['specialty'];
      this.phone = _data['phone'];
      this.email = _data['email'];
      this.fullAddress = _data['fullAddress'];
      this.fax = _data['fax'];
    }
  }

  static fromJS(data: any): ProviderSearchResult {
    data = typeof data === 'object' ? data : {};
    let result = new ProviderSearchResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['providerId'] = this.providerId;
    data['providerSourceId'] = this.providerSourceId;
    data['providerSource'] = this.providerSource;
    data['clientPracId'] = this.clientPracId;
    data['npi'] = this.npi;
    data['firstName'] = this.firstName;
    data['middleName'] = this.middleName;
    data['lastName'] = this.lastName;
    data['address1'] = this.address1;
    data['address2'] = this.address2;
    data['city'] = this.city;
    data['state'] = this.state;
    data['zip'] = this.zip;
    data['specialtyId'] = this.specialtyId;
    data['specialty'] = this.specialty;
    data['phone'] = this.phone;
    data['email'] = this.email;
    data['fullAddress'] = this.fullAddress;
    data['fax'] = this.fax;
    return data;
  }
}

export interface IProviderSearchResult {
  providerId?: number;
  providerSourceId?: number;
  providerSource?: string | undefined;
  clientPracId?: string | undefined;
  npi?: string | undefined;
  firstName?: string | undefined;
  middleName?: string | undefined;
  lastName?: string | undefined;
  address1?: string | undefined;
  address2?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  zip?: string | undefined;
  specialtyId?: number | undefined;
  specialty?: string | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  fullAddress?: string | undefined;
  fax?: string | undefined;
}

export class ProviderSearchResultPagedResult implements IProviderSearchResultPagedResult {
  items?: ProviderSearchResult[] | undefined;
  readonly pageIndex?: number;
  readonly pageSize?: number;
  totalItems?: number;
  readonly totalPages?: number;
  readonly hasPreviousPage?: boolean;
  readonly hasNextPage?: boolean;

  constructor(data?: IProviderSearchResultPagedResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['items'])) {
        this.items = [] as any;
        for (let item of _data['items']) this.items!.push(ProviderSearchResult.fromJS(item));
      }
      (this as any).pageIndex = _data['pageIndex'];
      (this as any).pageSize = _data['pageSize'];
      this.totalItems = _data['totalItems'];
      (this as any).totalPages = _data['totalPages'];
      (this as any).hasPreviousPage = _data['hasPreviousPage'];
      (this as any).hasNextPage = _data['hasNextPage'];
    }
  }

  static fromJS(data: any): ProviderSearchResultPagedResult {
    data = typeof data === 'object' ? data : {};
    let result = new ProviderSearchResultPagedResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.items)) {
      data['items'] = [];
      for (let item of this.items) data['items'].push(item ? item.toJSON() : (undefined as any));
    }
    data['pageIndex'] = this.pageIndex;
    data['pageSize'] = this.pageSize;
    data['totalItems'] = this.totalItems;
    data['totalPages'] = this.totalPages;
    data['hasPreviousPage'] = this.hasPreviousPage;
    data['hasNextPage'] = this.hasNextPage;
    return data;
  }
}

export interface IProviderSearchResultPagedResult {
  items?: ProviderSearchResult[] | undefined;
  pageIndex?: number;
  pageSize?: number;
  totalItems?: number;
  totalPages?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
}

export class SearchSortParams implements ISearchSortParams {
  searchText?: string | undefined;
  searchColumns?: string[] | undefined;
  sortColumn?: string | undefined;
  sortDirection?: string | undefined;

  constructor(data?: ISearchSortParams) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.searchText = _data['searchText'];
      if (Array.isArray(_data['searchColumns'])) {
        this.searchColumns = [] as any;
        for (let item of _data['searchColumns']) this.searchColumns!.push(item);
      }
      this.sortColumn = _data['sortColumn'];
      this.sortDirection = _data['sortDirection'];
    }
  }

  static fromJS(data: any): SearchSortParams {
    data = typeof data === 'object' ? data : {};
    let result = new SearchSortParams();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['searchText'] = this.searchText;
    if (Array.isArray(this.searchColumns)) {
      data['searchColumns'] = [];
      for (let item of this.searchColumns) data['searchColumns'].push(item);
    }
    data['sortColumn'] = this.sortColumn;
    data['sortDirection'] = this.sortDirection;
    return data;
  }
}

export interface ISearchSortParams {
  searchText?: string | undefined;
  searchColumns?: string[] | undefined;
  sortColumn?: string | undefined;
  sortDirection?: string | undefined;
}

export enum SubmissionIntent {
  _0 = 0,
  _1 = 1,
}

export class UpdateAssessmentMemberPCPRequest implements IUpdateAssessmentMemberPCPRequest {
  assessmentId!: number;
  memberId!: number;
  practitionerId?: number | undefined;
  practitionerNPI?: string | undefined;
  providerSourceId!: number;
  firstName?: string | undefined;
  middleName?: string | undefined;
  lastName?: string | undefined;
  address1!: string;
  address2?: string | undefined;
  city!: string;
  state!: string;
  zip?: string | undefined;
  specialtyId?: number | undefined;
  specialty?: string | undefined;
  phone?: string | undefined;
  fax?: string | undefined;
  email?: string | undefined;
  tin?: string | undefined;
  effectiveDate?: string | undefined;
  isNPUpdated?: boolean;

  constructor(data?: IUpdateAssessmentMemberPCPRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.assessmentId = _data['assessmentId'];
      this.memberId = _data['memberId'];
      this.practitionerId = _data['practitionerId'];
      this.practitionerNPI = _data['practitionerNPI'];
      this.providerSourceId = _data['providerSourceId'];
      this.firstName = _data['firstName'];
      this.middleName = _data['middleName'];
      this.lastName = _data['lastName'];
      this.address1 = _data['address1'];
      this.address2 = _data['address2'];
      this.city = _data['city'];
      this.state = _data['state'];
      this.zip = _data['zip'];
      this.specialtyId = _data['specialtyId'];
      this.specialty = _data['specialty'];
      this.phone = _data['phone'];
      this.fax = _data['fax'];
      this.email = _data['email'];
      this.tin = _data['tin'];
      this.effectiveDate = _data['effectiveDate'];
      this.isNPUpdated = _data['isNPUpdated'];
    }
  }

  static fromJS(data: any): UpdateAssessmentMemberPCPRequest {
    data = typeof data === 'object' ? data : {};
    let result = new UpdateAssessmentMemberPCPRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['assessmentId'] = this.assessmentId;
    data['memberId'] = this.memberId;
    data['practitionerId'] = this.practitionerId;
    data['practitionerNPI'] = this.practitionerNPI;
    data['providerSourceId'] = this.providerSourceId;
    data['firstName'] = this.firstName;
    data['middleName'] = this.middleName;
    data['lastName'] = this.lastName;
    data['address1'] = this.address1;
    data['address2'] = this.address2;
    data['city'] = this.city;
    data['state'] = this.state;
    data['zip'] = this.zip;
    data['specialtyId'] = this.specialtyId;
    data['specialty'] = this.specialty;
    data['phone'] = this.phone;
    data['fax'] = this.fax;
    data['email'] = this.email;
    data['tin'] = this.tin;
    data['effectiveDate'] = this.effectiveDate;
    data['isNPUpdated'] = this.isNPUpdated;
    return data;
  }
}

export interface IUpdateAssessmentMemberPCPRequest {
  assessmentId: number;
  memberId: number;
  practitionerId?: number | undefined;
  practitionerNPI?: string | undefined;
  providerSourceId: number;
  firstName?: string | undefined;
  middleName?: string | undefined;
  lastName?: string | undefined;
  address1: string;
  address2?: string | undefined;
  city: string;
  state: string;
  zip?: string | undefined;
  specialtyId?: number | undefined;
  specialty?: string | undefined;
  phone?: string | undefined;
  fax?: string | undefined;
  email?: string | undefined;
  tin?: string | undefined;
  effectiveDate?: string | undefined;
  isNPUpdated?: boolean;
}

export class UpdateAssessmentVerificationRequest implements IUpdateAssessmentVerificationRequest {
  latitude?: number | undefined;
  longitude?: number | undefined;
  isLocationConfirmed?: boolean | undefined;
  locationDeclineReasonId?: number | undefined;
  locationDeclineNotes?: string | undefined;
  isIdentityVerified!: boolean;
  pcpConfirmationStatusId!: number;
  isEncounterDateVerified!: boolean;
  encounterDate!: string;
  mismatchReason?: string | undefined;

  constructor(data?: IUpdateAssessmentVerificationRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.latitude = _data['latitude'];
      this.longitude = _data['longitude'];
      this.isLocationConfirmed = _data['isLocationConfirmed'];
      this.locationDeclineReasonId = _data['locationDeclineReasonId'];
      this.locationDeclineNotes = _data['locationDeclineNotes'];
      this.isIdentityVerified = _data['isIdentityVerified'];
      this.pcpConfirmationStatusId = _data['pcpConfirmationStatusId'];
      this.isEncounterDateVerified = _data['isEncounterDateVerified'];
      this.encounterDate = _data['encounterDate'];
      this.mismatchReason = _data['mismatchReason'];
    }
  }

  static fromJS(data: any): UpdateAssessmentVerificationRequest {
    data = typeof data === 'object' ? data : {};
    let result = new UpdateAssessmentVerificationRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['latitude'] = this.latitude;
    data['longitude'] = this.longitude;
    data['isLocationConfirmed'] = this.isLocationConfirmed;
    data['locationDeclineReasonId'] = this.locationDeclineReasonId;
    data['locationDeclineNotes'] = this.locationDeclineNotes;
    data['isIdentityVerified'] = this.isIdentityVerified;
    data['pcpConfirmationStatusId'] = this.pcpConfirmationStatusId;
    data['isEncounterDateVerified'] = this.isEncounterDateVerified;
    data['encounterDate'] = this.encounterDate;
    data['mismatchReason'] = this.mismatchReason;
    return data;
  }
}

export interface IUpdateAssessmentVerificationRequest {
  latitude?: number | undefined;
  longitude?: number | undefined;
  isLocationConfirmed?: boolean | undefined;
  locationDeclineReasonId?: number | undefined;
  locationDeclineNotes?: string | undefined;
  isIdentityVerified: boolean;
  pcpConfirmationStatusId: number;
  isEncounterDateVerified: boolean;
  encounterDate: string;
  mismatchReason?: string | undefined;
}

export class WeatherConditionDescriptionResponse implements IWeatherConditionDescriptionResponse {
  text?: string | undefined;
  languageCode?: string | undefined;

  constructor(data?: IWeatherConditionDescriptionResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.text = _data['text'];
      this.languageCode = _data['languageCode'];
    }
  }

  static fromJS(data: any): WeatherConditionDescriptionResponse {
    data = typeof data === 'object' ? data : {};
    let result = new WeatherConditionDescriptionResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['text'] = this.text;
    data['languageCode'] = this.languageCode;
    return data;
  }
}

export interface IWeatherConditionDescriptionResponse {
  text?: string | undefined;
  languageCode?: string | undefined;
}

export class WeatherConditionResponse implements IWeatherConditionResponse {
  iconBaseUri?: string | undefined;
  description?: WeatherConditionDescriptionResponse;
  type?: string | undefined;

  constructor(data?: IWeatherConditionResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.iconBaseUri = _data['iconBaseUri'];
      this.description = _data['description']
        ? WeatherConditionDescriptionResponse.fromJS(_data['description'])
        : (undefined as any);
      this.type = _data['type'];
    }
  }

  static fromJS(data: any): WeatherConditionResponse {
    data = typeof data === 'object' ? data : {};
    let result = new WeatherConditionResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['iconBaseUri'] = this.iconBaseUri;
    data['description'] = this.description ? this.description.toJSON() : (undefined as any);
    data['type'] = this.type;
    return data;
  }
}

export interface IWeatherConditionResponse {
  iconBaseUri?: string | undefined;
  description?: WeatherConditionDescriptionResponse;
  type?: string | undefined;
}

export class WeatherCurrentConditionsResponse implements IWeatherCurrentConditionsResponse {
  latitude?: number;
  longitude?: number;
  city?: string | undefined;
  currentTime?: string | undefined;
  timeZone?: WeatherTimeZoneResponse;
  isDaytime?: boolean;
  weatherCondition?: WeatherConditionResponse;
  temperature?: WeatherTemperatureResponse;
  wind?: WeatherWindResponse;

  constructor(data?: IWeatherCurrentConditionsResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.latitude = _data['latitude'];
      this.longitude = _data['longitude'];
      this.city = _data['city'];
      this.currentTime = _data['currentTime'];
      this.timeZone = _data['timeZone'] ? WeatherTimeZoneResponse.fromJS(_data['timeZone']) : (undefined as any);
      this.isDaytime = _data['isDaytime'];
      this.weatherCondition = _data['weatherCondition']
        ? WeatherConditionResponse.fromJS(_data['weatherCondition'])
        : (undefined as any);
      this.temperature = _data['temperature']
        ? WeatherTemperatureResponse.fromJS(_data['temperature'])
        : (undefined as any);
      this.wind = _data['wind'] ? WeatherWindResponse.fromJS(_data['wind']) : (undefined as any);
    }
  }

  static fromJS(data: any): WeatherCurrentConditionsResponse {
    data = typeof data === 'object' ? data : {};
    let result = new WeatherCurrentConditionsResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['latitude'] = this.latitude;
    data['longitude'] = this.longitude;
    data['city'] = this.city;
    data['currentTime'] = this.currentTime;
    data['timeZone'] = this.timeZone ? this.timeZone.toJSON() : (undefined as any);
    data['isDaytime'] = this.isDaytime;
    data['weatherCondition'] = this.weatherCondition ? this.weatherCondition.toJSON() : (undefined as any);
    data['temperature'] = this.temperature ? this.temperature.toJSON() : (undefined as any);
    data['wind'] = this.wind ? this.wind.toJSON() : (undefined as any);
    return data;
  }
}

export interface IWeatherCurrentConditionsResponse {
  latitude?: number;
  longitude?: number;
  city?: string | undefined;
  currentTime?: string | undefined;
  timeZone?: WeatherTimeZoneResponse;
  isDaytime?: boolean;
  weatherCondition?: WeatherConditionResponse;
  temperature?: WeatherTemperatureResponse;
  wind?: WeatherWindResponse;
}

export class WeatherTemperatureResponse implements IWeatherTemperatureResponse {
  degrees?: number | undefined;
  unit?: string | undefined;

  constructor(data?: IWeatherTemperatureResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.degrees = _data['degrees'];
      this.unit = _data['unit'];
    }
  }

  static fromJS(data: any): WeatherTemperatureResponse {
    data = typeof data === 'object' ? data : {};
    let result = new WeatherTemperatureResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['degrees'] = this.degrees;
    data['unit'] = this.unit;
    return data;
  }
}

export interface IWeatherTemperatureResponse {
  degrees?: number | undefined;
  unit?: string | undefined;
}

export class WeatherTimeZoneResponse implements IWeatherTimeZoneResponse {
  id?: string | undefined;

  constructor(data?: IWeatherTimeZoneResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data['id'];
    }
  }

  static fromJS(data: any): WeatherTimeZoneResponse {
    data = typeof data === 'object' ? data : {};
    let result = new WeatherTimeZoneResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['id'] = this.id;
    return data;
  }
}

export interface IWeatherTimeZoneResponse {
  id?: string | undefined;
}

export class WeatherWindDirectionResponse implements IWeatherWindDirectionResponse {
  degrees?: number | undefined;
  cardinal?: string | undefined;

  constructor(data?: IWeatherWindDirectionResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.degrees = _data['degrees'];
      this.cardinal = _data['cardinal'];
    }
  }

  static fromJS(data: any): WeatherWindDirectionResponse {
    data = typeof data === 'object' ? data : {};
    let result = new WeatherWindDirectionResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['degrees'] = this.degrees;
    data['cardinal'] = this.cardinal;
    return data;
  }
}

export interface IWeatherWindDirectionResponse {
  degrees?: number | undefined;
  cardinal?: string | undefined;
}

export class WeatherWindResponse implements IWeatherWindResponse {
  direction?: WeatherWindDirectionResponse;
  speed?: WeatherWindSpeedResponse;
  gust?: WeatherWindSpeedResponse;

  constructor(data?: IWeatherWindResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.direction = _data['direction']
        ? WeatherWindDirectionResponse.fromJS(_data['direction'])
        : (undefined as any);
      this.speed = _data['speed'] ? WeatherWindSpeedResponse.fromJS(_data['speed']) : (undefined as any);
      this.gust = _data['gust'] ? WeatherWindSpeedResponse.fromJS(_data['gust']) : (undefined as any);
    }
  }

  static fromJS(data: any): WeatherWindResponse {
    data = typeof data === 'object' ? data : {};
    let result = new WeatherWindResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['direction'] = this.direction ? this.direction.toJSON() : (undefined as any);
    data['speed'] = this.speed ? this.speed.toJSON() : (undefined as any);
    data['gust'] = this.gust ? this.gust.toJSON() : (undefined as any);
    return data;
  }
}

export interface IWeatherWindResponse {
  direction?: WeatherWindDirectionResponse;
  speed?: WeatherWindSpeedResponse;
  gust?: WeatherWindSpeedResponse;
}

export class WeatherWindSpeedResponse implements IWeatherWindSpeedResponse {
  value?: number | undefined;
  unit?: string | undefined;

  constructor(data?: IWeatherWindSpeedResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.value = _data['value'];
      this.unit = _data['unit'];
    }
  }

  static fromJS(data: any): WeatherWindSpeedResponse {
    data = typeof data === 'object' ? data : {};
    let result = new WeatherWindSpeedResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['value'] = this.value;
    data['unit'] = this.unit;
    return data;
  }
}

export interface IWeatherWindSpeedResponse {
  value?: number | undefined;
  unit?: string | undefined;
}

export interface FileResponse {
  data: Blob;
  status: number;
  fileName?: string;
  headers?: { [name: string]: any };
}

export class SwaggerException extends Error {
  override message: string;
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(message: string, status: number, response: string, headers: { [key: string]: any }, result: any) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isSwaggerException = true;

  static isSwaggerException(obj: any): obj is SwaggerException {
    return obj.isSwaggerException === true;
  }
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): Observable<any> {
  if (result !== null && result !== undefined) return _observableThrow(result);
  else return _observableThrow(new SwaggerException(message, status, response, headers, null));
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next('');
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((event.target as any).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}
